import React, { useState, useMemo, useEffect } from "react";
import { FilterList as FilterIcon } from "@mui/icons-material";
import {
  Box,
  Typography,
  Paper,
  Chip,
  Modal,
  TextField,
  Button,
  Select,
  FormControl,
  InputLabel,
  Grid,
  Divider,
  Skeleton,
  Tooltip,
  CircularProgress,
  Alert,
  MenuItem,
  IconButton,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Drawer,
  SwipeableDrawer,
  useMediaQuery,
  useTheme,
  Fab,
  Zoom,
  Collapse,
  Stack,
  Badge,
} from "@mui/material";
import {
  CameraAlt as CameraIcon,
  Visibility as ViewIcon,
  CloudUpload as UploadIcon,
  LocationOn as LocationIcon,
  MyLocation as MyLocationIcon,
  CheckCircle as CompletedIcon,
  InfoOutlined as InfoOutlinedIcon,
  History as HistoryIcon,
  AddCircle as AddIcon,
  Clear as ClearIcon,
  Close as CloseIcon,
  Tune as TuneIcon,
  FilterAlt as FilterAltIcon,
  Person as PersonIcon,
  Phone as PhoneIcon,
  Map as MapIcon,
  Nature as NatureIcon,
  Assignment as AssignmentIcon,
  LocalFlorist as FloristIcon,
  CalendarMonth as CalendarIcon,
  Block as BlockIcon,
  PhoneAndroid as PhoneAndroidIcon,
  PendingRounded as PendingIcon,
} from "@mui/icons-material";
import { useUser } from "../../Context/UserContext";
import PostApi, { GetApi } from "../../Services/api/ApiCall";
import API_URLS from "../../Services/api/action";
import { showSuccess, showError, showWarning } from "../../utills/toasts";
import DynamicTable from "../Dynamic/DynamicTable";

const colors = {
  darkGreen: "#1B5E20",
  mediumGreen: "#2E7D32",
  lightGreen: "#4CAF50",
  paleGreen: "#E8F5E9",
  darkOrange: "#E65100",
  lightOrange: "#FF9800",
  paleOrange: "#FFF3E0",
  darkPurple: "#7B1FA2",
  lightPurple: "#F3E5F5",
};

// Real Device Detection
const isRealMobileDevice = () => {
  return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
    navigator.userAgent,
  );
};

// Custom Timeline Component
const CustomTimeline = ({ items }) => (
  <Box sx={{ pl: 2, borderLeft: "2px solid #e0e0e0", ml: 1 }}>
    {items.map((item, idx) => (
      <Box
        key={item.id || idx}
        sx={{ position: "relative", pb: idx < items.length - 1 ? 3 : 0 }}
      >
        <Box
          sx={{
            position: "absolute",
            left: -11,
            top: 4,
            width: 20,
            height: 20,
            borderRadius: "50%",
            bgcolor: colors.darkGreen,
            border: "3px solid #fff",
            boxShadow: "0 0 0 2px " + colors.lightGreen,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <CompletedIcon sx={{ fontSize: 10, color: "#fff" }} />
        </Box>
        <Box sx={{ ml: 2 }}>
          <Typography variant="subtitle2" fontWeight={600}>
            {item.title}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {item.date}
          </Typography>
          {item.details && <Box sx={{ mt: 0.5 }}>{item.details}</Box>}
        </Box>
      </Box>
    ))}
  </Box>
);

const InfoRow = ({ icon: Icon, label, value, xs = 6, color, bold }) => (
  <Grid item xs={xs}>
    <Box sx={{ display: "flex", alignItems: "flex-start", gap: 0.5 }}>
      {Icon && (
        <Icon
          sx={{ fontSize: 14, mt: 0.3, color: color || "#999", flexShrink: 0 }}
        />
      )}
      <Box sx={{ minWidth: 0 }}>
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ fontSize: "0.65rem", lineHeight: 1.2, display: "block" }}
        >
          {label}
        </Typography>
        <Typography
          variant="body2"
          fontWeight={bold ? 700 : 500}
          sx={{
            fontSize: { xs: "0.78rem", sm: "0.85rem" },
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            color: color || "text.primary",
          }}
        >
          {value || "—"}
        </Typography>
      </Box>
    </Box>
  </Grid>
);

const Inspection = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isTablet = useMediaQuery(theme.breakpoints.between("sm", "md"));
  const isDesktop = useMediaQuery(theme.breakpoints.up("md"));

  const [isRealMobile, setIsRealMobile] = useState(false);

  const { authData, token } = useUser();
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState("All");
  const [filterDateFrom, setFilterDateFrom] = useState("");
  const [filterDateTo, setFilterDateTo] = useState("");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalRecords, setTotalRecords] = useState(0);
  const [modals, setModals] = useState({ upload: false, view: false });
  const [selectedRow, setSelectedRow] = useState(null);
  const [filterDrawerOpen, setFilterDrawerOpen] = useState(false);

  const [previewFile, setPreviewFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [saplingSurveyCounts, setSaplingSurveyCounts] = useState({});
  const [inspectionAddress, setInspectionAddress] = useState("");
  const [latitude, setLatitude] = useState("");
  const [longitude, setLongitude] = useState("");
  const [photoLat, setPhotoLat] = useState(null);
  const [photoLng, setPhotoLng] = useState(null);
  const [locationLoading, setLocationLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState({ upload: false });
  const [createdInspectionId, setCreatedInspectionId] = useState(null);
  const [imageProcessing, setImageProcessing] = useState(false);
  const [desktopBlockAlert, setDesktopBlockAlert] = useState(false);

  useEffect(() => {
    setIsRealMobile(isRealMobileDevice());
  }, []);

  // ✅ Role check for Block Admin
  const isBlockInspector = authData?.role === "block_admin";
  const isViewer = [
    "superadmin",
    "department_admin",
    "district_admin",
  ].includes(authData?.role);

  // --- HELPERS ---
  const getDistanceFromLatLonInMeters = (lat1, lon1, lat2, lon2) => {
    var R = 6371;
    var dLat = deg2rad(lat2 - lat1);
    var dLon = deg2rad(lon2 - lon1);
    var a =
      Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos(deg2rad(lat1)) *
        Math.cos(deg2rad(lat2)) *
        Math.sin(dLon / 2) *
        Math.sin(dLon / 2);
    var c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    return R * c * 1000;
  };
  function deg2rad(deg) {
    return deg * (Math.PI / 180);
  }

  const fetchAddressFromOSM = async (lat, lng) => {
    try {
      const res = await fetch(
        `https://nominatim.openstreetmap.org/reverse?format=json&lat=${lat}&lon=${lng}`,
      );
      const data = await res.json();
      if (data && data.address) {
        const { village, town, suburb, county, district, state, road } =
          data.address;
        const addressParts = [
          road,
          village || town || suburb,
          district || county,
          state,
        ].filter(Boolean);
        setInspectionAddress(
          addressParts.join(", ") || data.display_name || "Address not found",
        );
      } else {
        setInspectionAddress("Address details not available");
      }
    } catch (err) {
      console.error("Error fetching address:", err);
      setInspectionAddress(`Lat: ${lat.toFixed(5)}, Lng: ${lng.toFixed(5)}`);
    }
  };

  const burnLocationIntoImage = (file, lat, lng) => {
    const reader = new FileReader();
    reader.onload = () => {
      const img = new Image();
      img.onload = () => {
        const canvas = document.createElement("canvas");
        canvas.width = img.width;
        canvas.height = img.height;
        const ctx = canvas.getContext("2d");
        ctx.drawImage(img, 0, 0);
        const text = `Lat: ${lat.toFixed(5)}, Lng: ${lng.toFixed(5)}`;
        const fontSize = Math.max(16, Math.floor(canvas.width / 20));
        ctx.font = `bold ${fontSize}px Arial`;
        const textMetrics = ctx.measureText(text);
        const padding = 10;
        const textHeight = fontSize + padding * 2;
        const textWidth = textMetrics.width + padding * 2;
        ctx.fillStyle = "rgba(0, 0, 0, 0.6)";
        ctx.fillRect(0, canvas.height - textHeight, textWidth, textHeight);
        ctx.fillStyle = "white";
        ctx.textBaseline = "middle";
        ctx.fillText(text, padding, canvas.height - textHeight / 2);
        canvas.toBlob(
          (blob) => {
            if (blob) {
              const modifiedFile = new File(
                [blob],
                `geo_tagged_${Date.now()}.jpg`,
                { type: "image/jpeg" },
              );
              setPreviewFile(modifiedFile);
              setPreviewUrl(canvas.toDataURL("image/jpeg"));
              setImageProcessing(false);
              showSuccess("Geo-tagged image generated & address fetched.");
            }
          },
          "image/jpeg",
          0.9,
        );
      };
      img.src = reader.result;
    };
    reader.readAsDataURL(file);
  };

  const handleGeoTaggedCapture = async (event) => {
    if (!isRealMobile) {
      setDesktopBlockAlert(true);
      event.target.value = "";
      return;
    }
    const file = event.target.files[0];
    if (!file) return;
    if (!navigator.geolocation) {
      showError("Geolocation not supported.");
      return;
    }
    setPreviewUrl(null);
    setPreviewFile(null);
    setImageProcessing(true);
    setLocationLoading(true);
    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        const currentLat = pos.coords.latitude;
        const currentLng = pos.coords.longitude;
        const baseLat = parseFloat(latitude);
        const baseLng = parseFloat(longitude);
        await fetchAddressFromOSM(currentLat, currentLng);
        setLatitude(currentLat.toString());
        setLongitude(currentLng.toString());
        setPhotoLat(currentLat);
        setPhotoLng(currentLng);
        if (baseLat && baseLng) {
          const distanceInMeters = getDistanceFromLatLonInMeters(
            baseLat,
            baseLng,
            currentLat,
            currentLng,
          );
          if (distanceInMeters > 10) {
            setImageProcessing(false);
            setLocationLoading(false);
            showError(
              `Location Mismatch! You are ${distanceInMeters.toFixed(1)}m away.`,
            );
            event.target.value = "";
            return;
          }
        }
        burnLocationIntoImage(file, currentLat, currentLng);
        setLocationLoading(false);
      },
      (error) => {
        setImageProcessing(false);
        setLocationLoading(false);
        let msg = "Unable to retrieve location.";
        if (error.code === 1) msg = "Location permission denied.";
        showError(msg);
      },
      { enableHighAccuracy: true, timeout: 15000, maximumAge: 0 },
    );
  };

  const fetchCurrentLocation = () => {
    if (!navigator.geolocation) {
      showWarning("Geolocation not supported.");
      return;
    }
    setLocationLoading(true);
    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        const lat = pos.coords.latitude;
        const lng = pos.coords.longitude;
        setLatitude(lat.toString());
        setLongitude(lng.toString());
        await fetchAddressFromOSM(lat, lng);
        setLocationLoading(false);
        showSuccess("Inspection location fetched!");
      },
      () => {
        setLocationLoading(false);
        showWarning("Could not fetch location.");
      },
      { enableHighAccuracy: true, timeout: 15000, maximumAge: 0 },
    );
  };

  const mapApiToUi = (apiOrder) => {
    const inspections = apiOrder.inspections || [];
    const sortedInspections = [...inspections].sort((a, b) => b.id - a.id);
    const latestInspection =
      sortedInspections.length > 0 ? sortedInspections[0] : null;

    let currentStatus = "Not Inspected";
    let inspectedDate = null;
    let inspectedByName = null; // ✅ Populated from backend
    let inspectionImg = null;
    let surveyCount = 0;
    let latestInspectionId = null;

    if (
      latestInspection &&
      latestInspection.uploads &&
      latestInspection.uploads.length > 0
    ) {
      latestInspectionId = latestInspection.id;
      inspectedDate = latestInspection.inspection_date;
      const latestUpload = latestInspection.uploads[0];

      inspectedByName = latestUpload.inspected_by_name || "Unknown"; // ✅ Holds Block Admin Name
      inspectionImg = latestUpload.image;
      surveyCount = latestUpload.survey_count || 0;

      currentStatus = "Inspected";
    }

    const itemsList = apiOrder.items || [];
    return {
      id: apiOrder.order_id,
      order_id: apiOrder.order_id,
      request_id: apiOrder.items?.[0]?.request_id || null,
      status: currentStatus,
      inspected_by: inspectedByName, // ✅ Displayed as "Inspected By"
      inspected_date: inspectedDate,
      inspection_image: inspectionImg,
      inspection_id: latestInspectionId,
      surveyed_count: surveyCount,
      inspection_history: sortedInspections.map((insp) => ({
        id: insp.id,
        inspection_date: insp.inspection_date,
        inspector_name: insp.uploads?.[0]?.inspected_by_name || "Unknown",
        uploads: insp.uploads || [],
        remarks: insp.remarks,
      })),
      items_summary:
        itemsList.length > 0
          ? itemsList
              .map((i) => `${i.species_name} (${i.final_quantity || 0})`)
              .join(", ")
          : "No items",
      farmer: {
        id: apiOrder.farmer?.id,
        name: apiOrder.farmer?.name || "Unknown",
        mobile: apiOrder.farmer?.mobile || "N/A",
        address: apiOrder.farmer?.address || "N/A",
        district: apiOrder.farmer?.location?.district || "N/A",
        block: apiOrder.farmer?.location?.block || "N/A",
        village: apiOrder.farmer?.location?.village || "N/A",
      },
      production_center: {
        name: apiOrder.production_center?.name || "Unknown PC",
        address: apiOrder.production_center?.address || "N/A",
        village_id: apiOrder.production_center?.village_id,
      },
      scheme: { name: apiOrder.scheme?.name || "N/A" },
      items: itemsList.map((item) => ({
        stock_id: item.stock_id,
        species_name: item.species_name,
        species_name_tamil: item.species_name_tamil || item.species_name,
        final_quantity: item.final_quantity || 0,
      })),
      inspections,
    };
  };

  useEffect(() => {
    const fetchOrders = async () => {
      if (!authData?.user_id || !authData?.role) return;
      setLoading(true);
      try {
        const url = `${API_URLS.GetFormersOrders}${authData.user_id}/${authData.role}?page=${page + 1}&limit=${rowsPerPage}`;
        const res = await GetApi(url, token);
        if (res?.success && res.data) {
          setTotalRecords(res.total_records || 0);
          if (page === 0) setData(res.data.map(mapApiToUi));
          else
            setData((prev) => [
              ...prev,
              ...res.data
                .filter((item) => !prev.some((p) => p.id === item.order_id))
                .map(mapApiToUi),
            ]);
        }
      } catch (err) {
        showError("Failed to load orders.");
      } finally {
        setLoading(false);
      }
    };
    fetchOrders();
  }, [authData.user_id, authData.role, token, page, rowsPerPage]);

  const canInspect = (row) => {
    if (!isBlockInspector) return false;
    return true;
  };

  const filteredData = useMemo(() => {
    let result = [...data];
    if (filterStatus !== "All")
      result = result.filter((r) => r.status === filterStatus);
    if (filterDateFrom || filterDateTo) {
      result = result.filter((r) => {
        const rowDate = r.inspected_date ? new Date(r.inspected_date) : null;
        if (!rowDate) return false;
        const fromDate = filterDateFrom ? new Date(filterDateFrom) : null;
        const toDate = filterDateTo ? new Date(filterDateTo) : null;
        if (fromDate) {
          fromDate.setHours(0, 0, 0, 0);
          if (rowDate < fromDate) return false;
        }
        if (toDate) {
          toDate.setHours(23, 59, 59, 999);
          if (rowDate > toDate) return false;
        }
        return true;
      });
    }
    result.sort((a, b) => {
      if (a.status === "Not Inspected" && b.status !== "Not Inspected")
        return -1;
      if (a.status !== "Not Inspected" && b.status === "Not Inspected")
        return 1;
      return 0;
    });
    return result;
  }, [data, filterStatus, filterDateFrom, filterDateTo]);

  const stats = useMemo(
    () => ({
      total: data.length,
      notInspected: data.filter((d) => d.status === "Not Inspected").length,
      inspected: data.filter((d) => d.status === "Inspected").length,
    }),
    [data],
  );

  const clearAllFilters = () => {
    setFilterStatus("All");
    setFilterDateFrom("");
    setFilterDateTo("");
    setFilterDrawerOpen(false);
  };
  const isAnyFilterActive =
    filterStatus !== "All" || filterDateFrom || filterDateTo;

  const openModal = (type, row) => {
    if (type === "upload" && !isRealMobile) {
      setDesktopBlockAlert(true);
      return;
    }
    setSelectedRow(row);
    setModals((p) => ({ ...p, [type]: true }));
    if (type === "upload") {
      setPreviewFile(null);
      setPreviewUrl(null);
      setPhotoLat(null);
      setPhotoLng(null);
      setInspectionAddress(row?.farmer.address || "");
      setLatitude("");
      setLongitude("");
      setCreatedInspectionId(null);
      const init = {};
      row?.items?.forEach((i) => (init[i.stock_id] = ""));
      setSaplingSurveyCounts(init);
      fetchCurrentLocation();
    }
  };
  const closeModal = (type) => {
    setModals((p) => ({ ...p, [type]: false }));
    if (type === "upload") {
      setPreviewFile(null);
      setPreviewUrl(null);
      setPhotoLat(null);
      setPhotoLng(null);
    }
  };

  const handleUploadSave = async () => {
    if (!previewFile) {
      showError("Upload geo-tagged image.");
      return;
    }
    if (!photoLat || !photoLng) {
      showError("Geo-tag data missing.");
      return;
    }
    if (!inspectionAddress.trim()) {
      showError("Address required.");
      return;
    }
    let hasAny = false,
      exceeds = false;
    selectedRow.items.forEach((item) => {
      const v = parseInt(saplingSurveyCounts[item.stock_id]) || 0;
      if (v > 0) hasAny = true;
      if (v > (item.final_quantity || 0)) exceeds = true;
    });
    if (!hasAny) {
      showError("Enter sapling count.");
      return;
    }
    if (exceeds) {
      showError("Exceeds max.");
      return;
    }
    setActionLoading((p) => ({ ...p, upload: true }));
    try {
      let inspectionId = createdInspectionId;

      // ✅ Step 1: Create Inspection record if not exists
      if (!inspectionId) {
        const createRes = await PostApi(
          API_URLS.CreateInspection,
          {
            request_id: selectedRow.request_id,
            farmer_id: selectedRow.farmer.id,
            field_inspector_id: authData.user_id, // ✅ Sends Block Admin ID as field_inspector_id
          },
          token,
        );
        if (!createRes?.success && !createRes?.inspection_id)
          throw new Error(createRes?.message || "Failed to create");
        inspectionId = createRes.inspection_id;
        setCreatedInspectionId(inspectionId);
      }

      const totalSurveyed = Object.values(saplingSurveyCounts).reduce(
        (sum, val) => sum + (parseInt(val) || 0),
        0,
      );

      // ✅ Step 2: Upload Details
      const formData = new FormData();
      formData.append("inspection_id", inspectionId);
      formData.append("inspection_address", inspectionAddress);
      formData.append("latitude", photoLat);
      formData.append("longitude", photoLng);
      formData.append("survey_count", totalSurveyed.toString());
      formData.append("inspected_by", authData.user_id); // ✅ Sends Block Admin ID as inspected_by
      formData.append("image", previewFile);
      formData.append(
        "sapplings",
        JSON.stringify(
          selectedRow.items.map((i) => ({
            sapplingname: i.species_name,
            survey_count: parseInt(saplingSurveyCounts[i.stock_id]) || 0,
          })),
        ),
      );

      await PostApi(API_URLS.UploadInspectionSappling, formData, token);
      showSuccess("Submitted successfully!");
      closeModal("upload");
      refetchData();
    } catch (err) {
      showError(err?.message || "Failed.");
    } finally {
      setActionLoading((p) => ({ ...p, upload: false }));
    }
  };

  const refetchData = async () => {
    try {
      const res = await GetApi(
        `${API_URLS.GetFormersOrders}${authData.user_id}/${authData.role}?page=${page + 1}&limit=${rowsPerPage}`,
        token,
      );
      if (res?.success) {
        setTotalRecords(res.total_records);
        setData(res.data.map(mapApiToUi));
      }
    } catch (e) {}
  };

  const totalSaplings = (items) =>
    items?.reduce((s, i) => s + (i.final_quantity || 0), 0) || 0;
  const formatDate = (d) =>
    d
      ? new Date(d).toLocaleDateString("en-GB", {
          day: "2-digit",
          month: "short",
          year: "numeric",
        })
      : "N/A";
  const totalSurveyedFromFields = Object.values(saplingSurveyCounts).reduce(
    (sum, val) => sum + (parseInt(val) || 0),
    0,
  );

  const getStatusChip = (status) => {
    const s = {
      "Not Inspected": { bg: colors.paleOrange, color: colors.darkOrange },
      Inspected: { bg: colors.paleGreen, color: colors.darkGreen },
    };
    return (
      <Chip
        label={status}
        size="small"
        sx={{
          fontWeight: 600,
          fontSize: { xs: "0.65rem", sm: "0.75rem" },
          height: { xs: 22, sm: 26 },
          ...(s[status] || { bg: "#f5f5f5", color: "#666" }),
        }}
      />
    );
  };

  const buildTimelineItems = (history) => {
    if (!history || history.length === 0) return [];
    return history.map((h) => ({
      id: h.id,
      title: h.inspector_name,
      date: formatDate(h.inspection_date),
      status: "inspected",
      details: (
        <Box>
          <Typography
            variant="caption"
            display="block"
            sx={{ mt: 0.5, fontSize: "0.7rem" }}
          >
            Survived: <strong>{h.uploads?.[0]?.survey_count || 0}</strong>{" "}
            saplings
          </Typography>
          {h.uploads?.[0]?.inspection_address && (
            <Typography
              variant="caption"
              color="text.secondary"
              display="block"
              sx={{ fontSize: "0.65rem" }}
            >
              📍 {h.uploads?.[0].inspection_address}
            </Typography>
          )}
        </Box>
      ),
    }));
  };

  const tableColumns = useMemo(() => {
    const baseColumns = [
      {
        field: "farmer",
        headerName: "Farmer / Order",
        render: (row) => (
          <Box>
            <Typography fontWeight={700} fontSize="0.85rem">
              {row.farmer.name}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {row.order_id}
            </Typography>
          </Box>
        ),
      },
      {
        field: "farmer.address",
        headerName: "Address",
        render: (row) => (
          <Box>
            <Typography
              variant="body2"
              noWrap
              title={row.farmer.address}
              sx={{
                maxWidth: 180,
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
            >
              {row.farmer.address}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {row.farmer.district}/{row.farmer.block}
            </Typography>
          </Box>
        ),
      },
      {
        field: "items",
        headerName: "Saplings",
        render: (row) => (
          <Box>
            <Typography
              variant="caption"
              sx={{ fontWeight: 700, color: colors.darkGreen }}
            >
              {totalSaplings(row.items)}
            </Typography>
            {row.surveyed_count > 0 && (
              <Typography
                variant="caption"
                color="text.secondary"
                display="block"
              >
                Surv: {row.surveyed_count}
              </Typography>
            )}
          </Box>
        ),
      },
      {
        field: "items_summary",
        headerName: "Species",
        render: (row) => (
          <Tooltip title={row.items_summary} arrow>
            <Typography
              variant="caption"
              sx={{
                display: "-webkit-box",
                WebkitLineClamp: 2,
                WebkitBoxOrient: "vertical",
                overflow: "hidden",
              }}
            >
              {row.items_summary}
            </Typography>
          </Tooltip>
        ),
      },
      {
        field: "status",
        headerName: "Status",
        render: (row) => getStatusChip(row.status),
      },
    ];

    // ✅ UPDATED: Column labeled "Inspected By"
    if (isViewer) {
      baseColumns.splice(4, 0, {
        field: "inspected_by",
        headerName: "Inspected By",
        render: (row) => (
          <Box>
            <Typography variant="caption">{row.inspected_by || "-"}</Typography>
            {row.inspected_date && (
              <Typography
                variant="caption"
                color="text.secondary"
                display="block"
              >
                {formatDate(row.inspected_date)}
              </Typography>
            )}
          </Box>
        ),
      });
    }
    return baseColumns;
  }, [isViewer]);

  // --- FILTER DRAWER CONTENT ---
  const FilterDrawerContent = () => (
    <Box
      sx={{
        p: { xs: 2.5, sm: 3 },
        height: "100%",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          mb: 3,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <FilterAltIcon sx={{ color: colors.darkGreen, fontSize: 22 }} />
          <Typography
            variant="h6"
            fontWeight={700}
            sx={{ fontSize: { xs: "1.1rem", sm: "1.25rem" } }}
          >
            Filters
          </Typography>
          {isAnyFilterActive && (
            <Chip
              label="Active"
              size="small"
              color="primary"
              variant="outlined"
              sx={{ height: 20, fontSize: "0.6rem", fontWeight: 700 }}
            />
          )}
        </Box>
        <IconButton onClick={() => setFilterDrawerOpen(false)} size="small">
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>

      <Box sx={{ mb: 3 }}>
        <Typography
          variant="subtitle2"
          fontWeight={700}
          sx={{ mb: 1, fontSize: "0.8rem", color: "#000000" }}
        >
          STATUS
        </Typography>
        <FormControl fullWidth size="small" variant="outlined">
          <InputLabel
            sx={{
              fontSize: "0.85rem",
              color: "#000000",
              fontWeight: 500,
              "&.Mui-focused": { color: "#000000" },
            }}
          >
            Status
          </InputLabel>
          <Select
            value={filterStatus}
            label="Status"
            onChange={(e) => setFilterStatus(e.target.value)}
            sx={{
              "& .MuiOutlinedInput-notchedOutline": {
                borderColor: "#4caf50",
                borderWidth: 1.5,
              },
              "&:hover .MuiOutlinedInput-notchedOutline": {
                borderColor: "#2e7d32 !important",
                borderWidth: 2,
              },
              "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                borderColor: "#1b5e20",
                borderWidth: 2,
              },
            }}
          >
            <MenuItem value="All">
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Box
                  sx={{
                    width: 8,
                    height: 8,
                    borderRadius: "50%",
                    bgcolor: "#9E9E9E",
                  }}
                />
                All Status
              </Box>
            </MenuItem>
            <MenuItem value="Not Inspected">
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Box
                  sx={{
                    width: 8,
                    height: 8,
                    borderRadius: "50%",
                    bgcolor: colors.darkOrange,
                  }}
                />
                Not Inspected
              </Box>
            </MenuItem>
            <MenuItem value="Inspected">
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Box
                  sx={{
                    width: 8,
                    height: 8,
                    borderRadius: "50%",
                    bgcolor: colors.darkGreen,
                  }}
                />
                Inspected
              </Box>
            </MenuItem>
          </Select>
        </FormControl>
      </Box>

      <Box sx={{ mb: 3 }}>
        <Typography
          variant="subtitle2"
          fontWeight={700}
          sx={{ mb: 1, fontSize: "0.8rem", color: "#000000" }}
        >
          INSPECTED FROM
        </Typography>
        <TextField
          fullWidth
          size="small"
          type="date"
          value={filterDateFrom}
          onChange={(e) => setFilterDateFrom(e.target.value)}
          InputLabelProps={{ shrink: true }}
          inputProps={{ max: filterDateTo || undefined }}
          sx={{
            "& .MuiOutlinedInput-root": {
              "& .MuiOutlinedInput-notchedOutline": {
                borderColor: "#4caf50",
                borderWidth: 1.5,
              },
              "&:hover .MuiOutlinedInput-notchedOutline": {
                borderColor: "#2e7d32 !important",
                borderWidth: 2,
              },
              "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                borderColor: "#1b5e20",
                borderWidth: 2,
              },
            },
            "& .MuiInputLabel-root": { color: "#000000", fontWeight: 500 },
            "& .MuiInputLabel-root.Mui-focused": { color: "#000000" },
          }}
        />
      </Box>

      <Box sx={{ mb: 3 }}>
        <Typography
          variant="subtitle2"
          fontWeight={700}
          sx={{ mb: 1, fontSize: "0.8rem", color: "#000000" }}
        >
          INSPECTED TO
        </Typography>
        <TextField
          fullWidth
          size="small"
          type="date"
          value={filterDateTo}
          onChange={(e) => setFilterDateTo(e.target.value)}
          InputLabelProps={{ shrink: true }}
          inputProps={{ min: filterDateFrom || undefined }}
          sx={{
            "& .MuiOutlinedInput-root": {
              "& .MuiOutlinedInput-notchedOutline": {
                borderColor: "#4caf50",
                borderWidth: 1.5,
              },
              "&:hover .MuiOutlinedInput-notchedOutline": {
                borderColor: "#2e7d32 !important",
                borderWidth: 2,
              },
              "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                borderColor: "#1b5e20",
                borderWidth: 2,
              },
            },
            "& .MuiInputLabel-root": { color: "#000000", fontWeight: 500 },
            "& .MuiInputLabel-root.Mui-focused": { color: "#000000" },
            "& input": { fontSize: "0.85rem" },
          }}
        />
      </Box>

      {isAnyFilterActive && (
        <Collapse in={isAnyFilterActive}>
          <Box sx={{ mb: 3 }}>
            <Typography
              variant="subtitle2"
              fontWeight={700}
              sx={{ mb: 1, fontSize: "0.75rem", color: "#999" }}
            >
              ACTIVE FILTERS
            </Typography>
            <Stack direction="row" flexWrap="wrap" spacing={1} useFlexGap>
              {filterStatus !== "All" && (
                <Chip
                  label={filterStatus}
                  size="small"
                  onDelete={() => setFilterStatus("All")}
                  sx={{ fontSize: "0.7rem" }}
                />
              )}
              {filterDateFrom && (
                <Chip
                  label={`From: ${new Date(filterDateFrom).toLocaleDateString("en-GB", { day: "2-digit", month: "short" })}`}
                  size="small"
                  onDelete={() => setFilterDateFrom("")}
                  sx={{ fontSize: "0.7rem", bgcolor: "#E3F2FD" }}
                />
              )}
              {filterDateTo && (
                <Chip
                  label={`To: ${new Date(filterDateTo).toLocaleDateString("en-GB", { day: "2-digit", month: "short" })}`}
                  size="small"
                  onDelete={() => setFilterDateTo("")}
                  sx={{ fontSize: "0.7rem", bgcolor: "#E3F2FD" }}
                />
              )}
            </Stack>
          </Box>
        </Collapse>
      )}

      <Box sx={{ flex: 1 }} />

      <Box
        sx={{
          pt: 2,
          borderTop: "1px solid #eee",
          display: "flex",
          flexDirection: "column",
          gap: 1.5,
        }}
      >
        <Button
          fullWidth
          variant="contained"
          onClick={() => setFilterDrawerOpen(false)}
          sx={{
            bgcolor: colors.darkGreen,
            fontWeight: 700,
            py: 1.2,
            borderRadius: 2,
            fontSize: { xs: "0.85rem", sm: "0.9rem" },
            "&:hover": { bgcolor: "#1B5E20" },
          }}
        >
          Apply Filters ({filteredData.length} results)
        </Button>
        {isAnyFilterActive && (
          <Button
            fullWidth
            variant="outlined"
            startIcon={<ClearIcon sx={{ fontSize: 16 }} />}
            onClick={clearAllFilters}
            sx={{
              borderColor: "#ddd",
              color: "#666",
              fontWeight: 600,
              py: 1,
              borderRadius: 2,
              fontSize: { xs: "0.8rem", sm: "0.85rem" },
              "&:hover": { borderColor: "#999", bgcolor: "#f9f9f9" },
            }}
          >
            Clear All Filters
          </Button>
        )}
      </Box>
    </Box>
  );

  return (
    <Box
      sx={{
        p: { xs: 1.5, sm: 2, md: 3 },
        bgcolor: "#f5f7f6",
        minHeight: "100%",
        pb: isMobile ? 10 : 3,
      }}
    >
      {/* ===== HEADER ===== */}
      <Box
        sx={{
          mb: { xs: 2, sm: 3 },
          display: "flex",
          alignItems: { xs: "center", sm: "flex-start" },
          justifyContent: "space-between",
          gap: 1,
        }}
      >
        <Box sx={{ minWidth: 0 }}>
          <Typography
            variant="h5"
            fontWeight={800}
            color="#1B5E20"
            sx={{
              fontSize: { xs: "1.15rem", sm: "1.4rem", md: "1.5rem" },
              lineHeight: 1.3,
            }}
          >
            {isBlockInspector
              ? " Block Inspection Tasks"
              : " Inspection Overview"}
          </Typography>
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ fontSize: { xs: "0.7rem", sm: "0.8rem" }, mt: 0.3 }}
          >
            {isBlockInspector
              ? "View and inspect orders assigned to your block"
              : "View inspection status submitted by Block Admins"}
          </Typography>
        </Box>
        {isMobile && (
          <Zoom in>
            <Badge
              badgeContent={isAnyFilterActive ? 1 : 0}
              color="primary"
              variant="dot"
              sx={{
                "& .MuiBadge-dot": { width: 8, height: 8, bgcolor: "#FF5722" },
              }}
            >
              <Fab
                size="small"
                color="primary"
                onClick={() => setFilterDrawerOpen(true)}
                sx={{
                  bgcolor: colors.darkGreen,
                  width: 42,
                  height: 42,
                  "&:hover": { bgcolor: "#1B5E20" },
                  boxShadow: "0 3px 12px rgba(27,94,32,0.35)",
                }}
              >
                <TuneIcon sx={{ fontSize: 20 }} />
              </Fab>
            </Badge>
          </Zoom>
        )}
      </Box>

      {/* ===== STATS ===== */}
      <Box
        sx={{
          display: "flex",
          flexWrap: "wrap",
          gap: 1.5,
          mb: 4,
          justifyContent: { xs: "space-between", sm: "flex-start" },
        }}
      >
        {[
          {
            label: "Total Orders",
            value: stats.total,
            color: "#4F46E5",
            bg: "rgba(79, 70, 229, 0.08)",
            icon: AssignmentIcon,
          },
          {
            label: "Not Inspected",
            value: stats.notInspected,
            color: "#F97316",
            bg: "rgba(249, 115, 22, 0.10)",
            icon: PendingIcon,
          },
          {
            label: "Inspections Completed",
            value: stats.inspected,
            color: "#16A34A",
            bg: "rgba(22, 163, 74, 0.10)",
            icon: CompletedIcon,
          },
        ].map((stat, idx) => (
          <Box
            key={idx}
            sx={{
              flex: {
                xs: "1 1 calc(33.33% - 8px)",
                sm: "1 1 calc(33.33% - 12px)",
                md: "1 1 calc(33.33% - 12px)",
              },
              display: "flex",
              justifyContent: "center",
            }}
          >
            <Paper
              sx={{
                width: "100%",
                p: { xs: 1, sm: 1.5 },
                borderRadius: 2,
                textAlign: "center",
                bgcolor: stat.bg,
                border: `1px solid ${stat.bg}`,
                transition: "0.25s ease",
                "&:hover": {
                  transform: "translateY(-2px)",
                  boxShadow: "0 6px 14px rgba(0,0,0,0.08)",
                },
              }}
            >
              <stat.icon
                sx={{
                  fontSize: { xs: 16, sm: 20 },
                  color: stat.color,
                  mb: 1,
                  opacity: 0.7,
                }}
              />
              <Typography
                sx={{
                  color: stat.color,
                  fontWeight: 700,
                  fontSize: { xs: "1rem", sm: "1.2rem" },
                  lineHeight: 1.2,
                }}
              >
                {stat.value}
              </Typography>
              <Typography sx={{ fontSize: "12px", color: "#000" }}>
                {stat.label}
              </Typography>
            </Paper>
          </Box>
        ))}
      </Box>

      {/* ===== DESKTOP FILTERS ===== */}
      {isDesktop && (
        <Paper
          sx={{
            p: 2.5,
            mb: 3,
            borderRadius: 3,
            bgcolor: "#fff",
            border: "1px solid #e0e0e0",
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              mb: 2,
            }}
          >
            <Typography
              variant="subtitle2"
              fontWeight={700}
              sx={{ display: "flex", alignItems: "center", gap: 0.5 }}
            >
              <FilterIcon sx={{ fontSize: 20, color: colors.darkGreen }} />{" "}
              Filters
              {isAnyFilterActive && (
                <Chip
                  label="Active"
                  size="small"
                  color="primary"
                  variant="outlined"
                  sx={{
                    ml: 1,
                    height: 22,
                    fontSize: "0.65rem",
                    fontWeight: 600,
                  }}
                />
              )}
            </Typography>
            {isAnyFilterActive && (
              <Button
                size="small"
                variant="text"
                startIcon={<ClearIcon sx={{ fontSize: 16 }} />}
                onClick={clearAllFilters}
                sx={{
                  color: "#666",
                  textTransform: "none",
                  fontSize: "0.75rem",
                }}
              >
                Clear All
              </Button>
            )}
          </Box>
          <Grid container spacing={2} alignItems="flex-end">
            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel>Status</InputLabel>
                <Select
                  value={filterStatus}
                  label="Status"
                  onChange={(e) => setFilterStatus(e.target.value)}
                  sx={{ bgcolor: filterStatus !== "All" ? "#E8F5E9" : "#fff" }}
                >
                  <MenuItem value="All">
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <Box
                        sx={{
                          width: 10,
                          height: 10,
                          borderRadius: "50%",
                          bgcolor: "#9E9E9E",
                        }}
                      />
                      All Status
                    </Box>
                  </MenuItem>
                  <MenuItem value="Not Inspected">
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <Box
                        sx={{
                          width: 10,
                          height: 10,
                          borderRadius: "50%",
                          bgcolor: colors.darkOrange,
                        }}
                      />
                      Not Inspected
                    </Box>
                  </MenuItem>
                  <MenuItem value="Inspected">
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <Box
                        sx={{
                          width: 10,
                          height: 10,
                          borderRadius: "50%",
                          bgcolor: colors.darkGreen,
                        }}
                      />
                      Inspected
                    </Box>
                  </MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                fullWidth
                size="small"
                type="date"
                label="Inspected From"
                value={filterDateFrom}
                onChange={(e) => setFilterDateFrom(e.target.value)}
                InputLabelProps={{ shrink: true }}
                inputProps={{ max: filterDateTo || undefined }}
                sx={{
                  bgcolor: filterDateFrom ? "#E3F2FD" : "#fff",
                  "& .MuiOutlinedInput-root": {
                    "& fieldset": { borderColor: "#0f6e3a" },
                  },
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                fullWidth
                size="small"
                type="date"
                label="Inspected To"
                value={filterDateTo}
                onChange={(e) => setFilterDateTo(e.target.value)}
                InputLabelProps={{ shrink: true }}
                inputProps={{ min: filterDateFrom || undefined }}
                sx={{
                  bgcolor: filterDateTo ? "#E3F2FD" : "#fff",
                  "& .MuiOutlinedInput-root": {
                    "& fieldset": { borderColor: "#0f6e3a" },
                  },
                }}
              />
            </Grid>
          </Grid>
          {isAnyFilterActive && (
            <Box
              sx={{
                display: "flex",
                flexWrap: "wrap",
                gap: 1,
                mt: 2,
                pt: 2,
                borderTop: "1px dashed #e0e0e0",
              }}
            >
              {filterStatus !== "All" && (
                <Chip
                  label={`Status: ${filterStatus}`}
                  size="small"
                  onDelete={() => setFilterStatus("All")}
                />
              )}
              {filterDateFrom && (
                <Chip
                  label={`From: ${new Date(filterDateFrom).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" })}`}
                  size="small"
                  onDelete={() => setFilterDateFrom("")}
                  sx={{ bgcolor: "#E3F2FD" }}
                />
              )}
              {filterDateTo && (
                <Chip
                  label={`To: ${new Date(filterDateTo).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" })}`}
                  size="small"
                  onDelete={() => setFilterDateTo("")}
                  sx={{ bgcolor: "#E3F2FD" }}
                />
              )}
            </Box>
          )}
        </Paper>
      )}

      {/* ===== MOBILE/TABLET FILTER PILLS ===== */}
      {(isMobile || isTablet) && !isDesktop && isAnyFilterActive && (
        <Box
          sx={{
            display: "flex",
            gap: 1,
            mb: 2,
            overflowX: "auto",
            px: 0.5,
            pb: 0.5,
            "::-webkit-scrollbar": { display: "none" },
            scrollbarWidth: "none",
          }}
        >
          {filterStatus !== "All" && (
            <Chip
              label={filterStatus}
              size="small"
              onDelete={() => setFilterStatus("All")}
              sx={{ fontSize: "0.65rem", height: 24, flexShrink: 0 }}
            />
          )}
          {filterDateFrom && (
            <Chip
              label={`From: ${new Date(filterDateFrom).toLocaleDateString("en-GB", { day: "2-digit", month: "short" })}`}
              size="small"
              onDelete={() => setFilterDateFrom("")}
              sx={{
                fontSize: "0.65rem",
                height: 24,
                bgcolor: "#E3F2FD",
                flexShrink: 0,
              }}
            />
          )}
          {filterDateTo && (
            <Chip
              label={`To: ${new Date(filterDateTo).toLocaleDateString("en-GB", { day: "2-digit", month: "short" })}`}
              size="small"
              onDelete={() => setFilterDateTo("")}
              sx={{
                fontSize: "0.65rem",
                height: 24,
                bgcolor: "#E3F2FD",
                flexShrink: 0,
              }}
            />
          )}
          <Chip
            label="Clear"
            size="small"
            onClick={clearAllFilters}
            sx={{
              fontSize: "0.65rem",
              height: 24,
              bgcolor: "#f5f5f5",
              flexShrink: 0,
            }}
          />
        </Box>
      )}

      {/* ===== DESKTOP TABLE ===== */}
      <Box sx={{ display: { xs: "none", md: "block" } }}>
        <DynamicTable
          data={filteredData}
          columns={[
            ...tableColumns,
            {
              field: "actions",
              headerName: "Actions",
              render: (row) => (
                <Box
                  sx={{ display: "flex", gap: 0.5, justifyContent: "center" }}
                >
                  <IconButton
                    size="small"
                    color="primary"
                    onClick={() => openModal("view", row)}
                  >
                    <ViewIcon fontSize="small" />
                  </IconButton>
                  {isBlockInspector && canInspect(row) && (
                    <Tooltip
                      title={
                        !isRealMobile
                          ? "Mobile Only"
                          : row.inspection_id
                            ? "Re-inspect"
                            : "Start Inspection"
                      }
                    >
                      <span>
                        <IconButton
                          size="small"
                          onClick={() => openModal("upload", row)}
                          sx={{
                            color: !isRealMobile ? "#999" : colors.darkPurple,
                          }}
                          disabled={!isRealMobile}
                        >
                          {row.inspection_id ? (
                            <UploadIcon fontSize="small" />
                          ) : (
                            <AddIcon fontSize="small" />
                          )}
                        </IconButton>
                      </span>
                    </Tooltip>
                  )}
                </Box>
              ),
            },
          ]}
          loading={loading}
          rowsPerPageOptions={[5, 10, 25]}
          defaultRowsPerPage={10}
          enableSearch={true}
          searchPlaceholder="Search by Farmer, Order ID..."
          viewMode={true}
        />
      </Box>

      {/* ===== MOBILE/TABLET CARDS ===== */}
      <Box sx={{ display: { xs: "block", md: "none" } }}>
        {loading ? (
          [1, 2, 3].map((i) => (
            <Paper key={i} sx={{ p: 2, mb: 2, borderRadius: 2 }}>
              <Skeleton variant="rect" height={20} width="60%" sx={{ mb: 1 }} />
              <Skeleton
                variant="rect"
                height={14}
                width="40%"
                sx={{ mb: 1.5 }}
              />
              <Skeleton
                variant="rect"
                height={60}
                sx={{ mb: 1.5, borderRadius: 1 }}
              />
              <Skeleton variant="rect" height={32} width="80%" />
            </Paper>
          ))
        ) : filteredData.length === 0 ? (
          <Paper
            sx={{ p: { xs: 3, sm: 4 }, textAlign: "center", borderRadius: 2 }}
          >
            <Typography
              variant="h6"
              color="text.secondary"
              gutterBottom
              sx={{ fontSize: { xs: "1rem", sm: "1.1rem" } }}
            >
              No Records Found
            </Typography>
            <Typography
              variant="body2"
              color="text.disabled"
              sx={{ fontSize: { xs: "0.75rem", sm: "0.85rem" } }}
            >
              {isAnyFilterActive
                ? "Try adjusting your filters to see more results."
                : "No inspection data available."}
            </Typography>
            {isAnyFilterActive && (
              <Button
                size="small"
                variant="outlined"
                startIcon={<ClearIcon />}
                onClick={clearAllFilters}
                sx={{ mt: 2, fontSize: "0.8rem" }}
              >
                Clear Filters
              </Button>
            )}
          </Paper>
        ) : (
          filteredData.map((row) => (
            <Paper
              key={row.id}
              sx={{
                mb: { xs: 1.5, sm: 2 },
                borderRadius: { xs: 1.5, sm: 2 },
                boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
                overflow: "hidden",
              }}
            >
              <Box
                sx={{
                  bgcolor:
                    row.status === "Inspected"
                      ? colors.paleGreen
                      : colors.paleOrange,
                  px: { xs: 1.5, sm: 2 },
                  py: { xs: 1, sm: 1.2 },
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                <Box sx={{ minWidth: 0, flex: 1 }}>
                  <Typography
                    variant="subtitle1"
                    fontWeight={700}
                    sx={{
                      color:
                        row.status === "Inspected"
                          ? colors.darkGreen
                          : "#bf360c",
                      fontSize: { xs: "0.85rem", sm: "0.95rem" },
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {row.farmer.name}
                  </Typography>
                  <Typography
                    variant="caption"
                    sx={{
                      color: row.status === "Inspected" ? "#2E7D32" : "#bf360c",
                      fontSize: { xs: "0.6rem", sm: "0.7rem" },
                    }}
                  >
                    {row.order_id}
                  </Typography>
                </Box>
                {getStatusChip(row.status)}
              </Box>

              <Box sx={{ px: { xs: 1.5, sm: 2 }, py: { xs: 1.2, sm: 1.5 } }}>
                <Grid container spacing={{ xs: 0.8, sm: 1 }} sx={{ mb: 1.5 }}>
                  <InfoRow
                    icon={MapIcon}
                    label="Address"
                    value={row.farmer.address}
                    xs={12}
                  />
                  <InfoRow
                    icon={FloristIcon}
                    label="Saplings"
                    value={`${totalSaplings(row.items)} total${row.surveyed_count > 0 ? ` · Surv: ${row.surveyed_count}` : ""}`}
                    xs={6}
                    color={colors.darkGreen}
                    bold
                  />
                  <InfoRow
                    icon={CalendarIcon}
                    label="Inspected"
                    value={
                      row.inspected_date ? formatDate(row.inspected_date) : "—"
                    }
                    xs={6}
                    color={row.inspected_date ? "#1976D2" : undefined}
                  />
                  {/* ✅ UPDATED: Mobile Card "Inspected By" */}
                  {isViewer && row.inspected_by && (
                    <InfoRow
                      icon={PersonIcon}
                      label="Inspected By"
                      value={row.inspected_by}
                      xs={12}
                    />
                  )}
                </Grid>

                {row.items_summary !== "No items" && (
                  <Box
                    sx={{
                      mb: 1.5,
                      p: { xs: 1, sm: 1.2 },
                      bgcolor: "#f9fafb",
                      borderRadius: 1.5,
                      border: "1px solid #f0f0f0",
                    }}
                  >
                    <Typography
                      variant="caption"
                      sx={{
                        fontSize: "0.6rem",
                        color: "#999",
                        fontWeight: 600,
                        textTransform: "uppercase",
                        letterSpacing: 0.5,
                        display: "block",
                        mb: 0.3,
                      }}
                    >
                      Species
                    </Typography>
                    <Typography
                      variant="caption"
                      sx={{
                        fontSize: { xs: "0.72rem", sm: "0.8rem" },
                        color: "#444",
                        lineHeight: 1.4,
                        display: "-webkit-box",
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: "vertical",
                        overflow: "hidden",
                      }}
                    >
                      {row.items_summary}
                    </Typography>
                  </Box>
                )}

                <Divider sx={{ my: 1 }} />

                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "flex-end",
                    gap: { xs: 0.8, sm: 1 },
                    mt: 1,
                  }}
                >
                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={<ViewIcon sx={{ fontSize: 16 }} />}
                    onClick={() => openModal("view", row)}
                    sx={{
                      fontSize: { xs: "0.7rem", sm: "0.8rem" },
                      minWidth: 0,
                      px: { xs: 1.5, sm: 2 },
                      py: { xs: 0.4, sm: 0.5 },
                      borderRadius: 1.5,
                      borderColor: "#ddd",
                      color: "#555",
                      "&:hover": { borderColor: "#999", bgcolor: "#f5f5f5" },
                    }}
                  >
                    View
                  </Button>
                  {isBlockInspector && canInspect(row) && (
                    <Tooltip
                      title={
                        !isRealMobile
                          ? "Mobile Device Required"
                          : row.inspection_id
                            ? "Re-inspect"
                            : "Inspect"
                      }
                    >
                      <span>
                        <Button
                          size="small"
                          variant="contained"
                          startIcon={
                            row.inspection_id ? (
                              <UploadIcon sx={{ fontSize: 16 }} />
                            ) : (
                              <AddIcon sx={{ fontSize: 16 }} />
                            )
                          }
                          onClick={() => openModal("upload", row)}
                          disabled={!isRealMobile}
                          sx={{
                            bgcolor: !isRealMobile
                              ? "#9e9e9e"
                              : colors.darkPurple,
                            "&:hover": {
                              bgcolor: !isRealMobile ? "#9e9e9e" : "#6A1B9A",
                            },
                            fontSize: { xs: "0.7rem", sm: "0.8rem" },
                            minWidth: 0,
                            px: { xs: 1.5, sm: 2 },
                            py: { xs: 0.4, sm: 0.5 },
                            borderRadius: 1.5,
                            boxShadow: isRealMobile
                              ? "0 2px 6px rgba(123,31,162,0.3)"
                              : "none",
                            cursor: isRealMobile ? "pointer" : "not-allowed",
                          }}
                        >
                          {row.inspection_id ? "Re-inspect" : "Inspect"}
                        </Button>
                      </span>
                    </Tooltip>
                  )}
                </Box>
              </Box>
            </Paper>
          ))
        )}
      </Box>

      {/* ===== FILTER DRAWER ===== */}
      <SwipeableDrawer
        anchor="bottom"
        open={filterDrawerOpen}
        onClose={() => setFilterDrawerOpen(false)}
        onOpen={() => setFilterDrawerOpen(true)}
        disableDiscovery={isDesktop}
        sx={{
          "& .MuiDrawer-paper": {
            borderRadius: "16px 16px 0 0",
            maxHeight: "85vh",
            bgcolor: "#fff",
            borderTop: "2px solid #e0e0e0",
          },
        }}
        ModalProps={{ keepMounted: true }}
      >
        <Box sx={{ display: "flex", justifyContent: "center", pt: 1, pb: 0 }}>
          <Box
            sx={{ width: 40, height: 4, borderRadius: 2, bgcolor: "#ddd" }}
          />
        </Box>
        <FilterDrawerContent />
      </SwipeableDrawer>

      {/* ===== UPLOAD MODAL ===== */}
      <Modal
        open={modals.upload}
        onClose={() => closeModal("upload")}
        closeAfterTransition
      >
        <Box
          sx={{
            position: "absolute",
            bottom: 0,
            left: 0,
            right: 0,
            top: { xs: "5%", sm: "10%" },
            display: "flex",
            alignItems: { sm: "center" },
            justifyContent: "center",
          }}
        >
          <Paper
            sx={{
              width: { xs: "100%", sm: 700, md: 750 },
              height: { xs: "95%", sm: "auto" },
              maxHeight: { xs: "95vh", sm: "90vh" },
              borderRadius: { xs: "16px 16px 0 0", sm: 3 },
              p: { xs: 2, sm: 3, md: 4 },
              outline: "none",
              overflowY: "auto",
              boxShadow: {
                xs: "0 -4px 20px rgba(0,0,0,0.15)",
                sm: "0 8px 32px rgba(0,0,0,0.2)",
              },
            }}
          >
            {isMobile && (
              <Box sx={{ display: "flex", justifyContent: "center", mb: 2 }}>
                <Box
                  sx={{
                    width: 40,
                    height: 4,
                    borderRadius: 2,
                    bgcolor: "#ddd",
                  }}
                />
              </Box>
            )}

            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                mb: 2,
              }}
            >
              <Box>
                <Typography
                  variant="h6"
                  fontWeight={700}
                  color={colors.darkPurple}
                  sx={{ fontSize: { xs: "1rem", sm: "1.2rem" } }}
                >
                  {selectedRow?.inspection_id
                    ? "📷 Re-inspection"
                    : "📷 Start Inspection"}
                </Typography>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ fontSize: "0.7rem" }}
                >
                  Order: <b>{selectedRow?.order_id}</b> | Farmer:{" "}
                  <b>{selectedRow?.farmer?.name}</b>
                </Typography>
              </Box>
              <IconButton onClick={() => closeModal("upload")} size="small">
                <CloseIcon fontSize="small" />
              </IconButton>
            </Box>

            <Alert
              severity="success"
              sx={{
                mb: 2,
                borderRadius: 2,
                "& .MuiAlert-icon": { alignItems: "center" },
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <PhoneAndroidIcon
                  sx={{ fontSize: 18, color: colors.darkGreen }}
                />
                <Typography variant="caption" fontWeight={600}>
                  Mobile Device Verified — Camera & GPS Active
                </Typography>
              </Box>
            </Alert>

            <Alert severity="info" sx={{ mb: 2, borderRadius: 2 }}>
              <Typography variant="caption">
                Ensure you are at site. Photo must be taken within 10 meters.
              </Typography>
            </Alert>
            <Divider sx={{ my: 2 }} />

            <Grid container spacing={{ xs: 2, sm: 3 }}>
              {/* Left Column - Camera */}
              <Grid item xs={12} md={6}>
                <Typography
                  variant="subtitle2"
                  fontWeight={700}
                  sx={{ mb: 1, fontSize: "0.85rem" }}
                >
                  1. Geo-tagged Image
                </Typography>

                <Box
                  onClick={() =>
                    isRealMobile &&
                    document.getElementById("geo-upload").click()
                  }
                  sx={{
                    border: previewUrl
                      ? "2px solid #4CAF50"
                      : "2px dashed #ccc",
                    borderRadius: 2,
                    p: 1,
                    textAlign: "center",
                    bgcolor: "#fafafa",
                    cursor: isRealMobile ? "pointer" : "default",
                    "&:hover": {
                      bgcolor: isRealMobile ? colors.paleGreen : "#fafafa",
                    },
                    height: { xs: 160, sm: 180 },
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    mb: 2,
                    position: "relative",
                  }}
                >
                  {isRealMobile && (
                    <input
                      id="geo-upload"
                      type="file"
                      accept="image/*"
                      capture="environment"
                      hidden
                      onChange={handleGeoTaggedCapture}
                    />
                  )}
                  {imageProcessing ? (
                    <Box
                      sx={{
                        position: "absolute",
                        top: 0,
                        left: 0,
                        width: "100%",
                        height: "100%",
                        bgcolor: "rgba(255,255,255,0.85)",
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        justifyContent: "center",
                        borderRadius: 2,
                      }}
                    >
                      <CircularProgress size={28} />
                      <Typography
                        variant="caption"
                        sx={{ mt: 1, fontSize: "0.7rem" }}
                      >
                        Geo-tagging...
                      </Typography>
                    </Box>
                  ) : previewUrl ? (
                    <img
                      src={previewUrl}
                      alt="Preview"
                      style={{
                        width: "100%",
                        height: "100%",
                        objectFit: "cover",
                        borderRadius: 8,
                      }}
                    />
                  ) : (
                    <>
                      <CameraIcon
                        sx={{
                          fontSize: { xs: 32, sm: 40 },
                          color: isRealMobile ? "#999" : "#ccc",
                          mb: 0.5,
                        }}
                      />
                      <Typography
                        color={
                          isRealMobile ? "text.secondary" : "text.disabled"
                        }
                        sx={{ fontSize: { xs: "0.75rem", sm: "0.85rem" } }}
                      >
                        {isRealMobile ? "Tap to Capture" : "Camera Unavailable"}
                      </Typography>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ fontSize: "0.6rem" }}
                      >
                        Geo-tagged via OpenStreetMap
                      </Typography>
                    </>
                  )}
                </Box>

                {/* Location Section */}
                <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
                  <Typography
                    variant="subtitle2"
                    fontWeight={700}
                    sx={{ fontSize: "0.85rem", flex: 1 }}
                  >
                    Location
                  </Typography>
                  <Tooltip title="Refresh Address" arrow>
                    <IconButton
                      size="small"
                      onClick={fetchCurrentLocation}
                      disabled={locationLoading}
                      sx={{ color: colors.darkGreen }}
                    >
                      {locationLoading ? (
                        <CircularProgress size={16} />
                      ) : (
                        <MyLocationIcon sx={{ fontSize: 18 }} />
                      )}
                    </IconButton>
                  </Tooltip>
                </Box>
                <TextField
                  fullWidth
                  size="small"
                  label="Address"
                  value={inspectionAddress}
                  onChange={(e) => setInspectionAddress(e.target.value)}
                  helperText="Auto-fetched from location"
                  sx={{
                    mb: { xs: 1.5, sm: 2 },
                    "& .MuiInputBase-input": { fontSize: "0.8rem" },
                  }}
                />
                <Grid container spacing={1.5}>
                  <Grid item xs={6}>
                    <TextField
                      fullWidth
                      size="small"
                      label="Lat"
                      value={latitude}
                      disabled
                      helperText=" "
                      sx={{ "& .MuiInputBase-input": { fontSize: "0.8rem" } }}
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <TextField
                      fullWidth
                      size="small"
                      label="Lng"
                      value={longitude}
                      disabled
                      helperText=" "
                      sx={{ "& .MuiInputBase-input": { fontSize: "0.8rem" } }}
                    />
                  </Grid>
                </Grid>
                {photoLat && (
                  <Box
                    sx={{
                      mt: 1,
                      p: 1,
                      bgcolor: "#E8F5E9",
                      borderRadius: 1.5,
                      border: "1px solid #C8E6C9",
                    }}
                  >
                    <Typography
                      variant="caption"
                      sx={{ fontSize: "0.7rem", color: colors.darkGreen }}
                    >
                      <strong>✓ Photo Verified:</strong> Captured within 10m
                    </Typography>
                    <Typography
                      variant="caption"
                      display="block"
                      sx={{ fontSize: "0.6rem", color: "#666" }}
                    >
                      Photo: {photoLat.toFixed(5)}, {photoLng.toFixed(5)}
                    </Typography>
                  </Box>
                )}
              </Grid>

              {/* Right Column - Sapling Counts */}
              <Grid item xs={12} md={6}>
                <Typography
                  variant="subtitle2"
                  fontWeight={700}
                  sx={{ mb: 1, fontSize: "0.85rem" }}
                >
                  2. Survived Sapling Count
                </Typography>
                <Paper
                  sx={{
                    p: { xs: 1.5, sm: 2 },
                    bgcolor: "#f9f9f9",
                    borderRadius: 2,
                    maxHeight: { xs: 250, sm: 380 },
                    overflowY: "auto",
                  }}
                >
                  {selectedRow?.items?.map((item) => (
                    <Box key={item.stock_id} sx={{ mb: { xs: 1.5, sm: 2 } }}>
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                          mb: 0.3,
                        }}
                      >
                        <Typography
                          variant="body2"
                          sx={{
                            fontWeight: 600,
                            fontSize: { xs: "0.75rem", sm: "0.85rem" },
                          }}
                        >
                          {item.species_name}
                          <Typography
                            component="span"
                            variant="caption"
                            sx={{
                              color: "#777",
                              ml: 0.5,
                              fontSize: { xs: "0.6rem", sm: "0.7rem" },
                            }}
                          >
                            ({item.species_name_tamil})
                          </Typography>
                        </Typography>
                        <Typography
                          variant="caption"
                          sx={{
                            color: colors.darkGreen,
                            fontWeight: 700,
                            fontSize: "0.7rem",
                          }}
                        >
                          Max: {item.final_quantity || 0}
                        </Typography>
                      </Box>
                      <TextField
                        fullWidth
                        size="small"
                        type="number"
                        placeholder="Count"
                        value={saplingSurveyCounts[item.stock_id] || ""}
                        onChange={(e) => {
                          const v = parseInt(e.target.value);
                          if (e.target.value === "") {
                            setSaplingSurveyCounts((p) => ({
                              ...p,
                              [item.stock_id]: "",
                            }));
                            return;
                          }
                          if (isNaN(v) || v < 0) return;
                          if (v > (item.final_quantity || 0)) {
                            showError(`Exceeds max ${item.final_quantity}`);
                            return;
                          }
                          setSaplingSurveyCounts((p) => ({
                            ...p,
                            [item.stock_id]: v.toString(),
                          }));
                        }}
                        sx={{
                          "& .MuiInputBase-input": { fontSize: "0.85rem" },
                          bgcolor:
                            saplingSurveyCounts[item.stock_id] &&
                            parseInt(saplingSurveyCounts[item.stock_id]) > 0
                              ? "#E8F5E9"
                              : "#fff",
                        }}
                      />
                    </Box>
                  ))}
                  <Divider sx={{ my: 1 }} />
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                    }}
                  >
                    <Typography
                      variant="body2"
                      fontWeight={700}
                      sx={{ fontSize: "0.8rem" }}
                    >
                      Total:
                    </Typography>
                    <Typography
                      variant="body2"
                      fontWeight={800}
                      sx={{
                        color:
                          totalSurveyedFromFields > 0
                            ? colors.darkGreen
                            : "#999",
                        bgcolor:
                          totalSurveyedFromFields > 0
                            ? colors.paleGreen
                            : "transparent",
                        px: 1.5,
                        py: 0.5,
                        borderRadius: 1,
                        fontSize: "0.85rem",
                      }}
                    >
                      {totalSurveyedFromFields} /{" "}
                      {selectedRow ? totalSaplings(selectedRow.items) : 0}
                    </Typography>
                  </Box>
                </Paper>
              </Grid>
            </Grid>

            {/* Submit Buttons */}
            <Box
              sx={{
                display: "flex",
                gap: { xs: 1, sm: 2 },
                justifyContent: "flex-end",
                mt: { xs: 2, sm: 3 },
                pt: 2,
                borderTop: "1px solid #f0f0f0",
                position: { xs: "sticky", sm: "relative" },
                bottom: 0,
                bgcolor: "#fff",
                pb: { xs: 1, sm: 0 },
              }}
            >
              <Button
                variant="outlined"
                onClick={() => closeModal("upload")}
                sx={{
                  fontSize: { xs: "0.8rem", sm: "0.85rem" },
                  borderRadius: 2,
                  px: { xs: 2, sm: 3 },
                }}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                sx={{
                  bgcolor: colors.darkPurple,
                  "&:hover": { bgcolor: "#6A1B9A" },
                  fontSize: { xs: "0.8rem", sm: "0.85rem" },
                  borderRadius: 2,
                  px: { xs: 2, sm: 3 },
                  minWidth: { xs: 120, sm: "auto" },
                  boxShadow: "0 2px 8px rgba(123,31,162,0.3)",
                }}
                startIcon={
                  actionLoading.upload ? (
                    <CircularProgress size={18} color="inherit" />
                  ) : (
                    <UploadIcon sx={{ fontSize: 18 }} />
                  )
                }
                onClick={handleUploadSave}
                disabled={actionLoading.upload || !previewFile}
              >
                Submit
              </Button>
            </Box>
          </Paper>
        </Box>
      </Modal>

      {/* ===== VIEW MODAL ===== */}
      <Modal
        open={modals.view}
        onClose={() => closeModal("view")}
        closeAfterTransition
      >
        <Box
          sx={{
            position: "absolute",
            bottom: 0,
            left: 0,
            right: 0,
            top: { xs: "3%", sm: "5%" },
            display: "flex",
            alignItems: { sm: "center" },
            justifyContent: "center",
          }}
        >
          <Paper
            sx={{
              width: { xs: "100%", sm: 700, md: 750 },
              height: { xs: "97%", sm: "auto" },
              maxHeight: { xs: "97vh", sm: "92vh" },
              borderRadius: { xs: "16px 16px 0 0", sm: 3 },
              p: { xs: 2, sm: 3, md: 4 },
              outline: "none",
              overflowY: "auto",
              boxShadow: {
                xs: "0 -4px 20px rgba(0,0,0,0.15)",
                sm: "0 8px 32px rgba(0,0,0,0.2)",
              },
            }}
          >
            {isMobile && (
              <Box sx={{ display: "flex", justifyContent: "center", mb: 1 }}>
                <Box
                  sx={{
                    width: 40,
                    height: 4,
                    borderRadius: 2,
                    bgcolor: "#ddd",
                  }}
                />
              </Box>
            )}

            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                mb: 2,
              }}
            >
              <Typography
                variant="h6"
                fontWeight={700}
                color={colors.darkGreen}
                sx={{ fontSize: { xs: "1rem", sm: "1.2rem" } }}
              >
                Order Details
              </Typography>
              <IconButton onClick={() => closeModal("view")} size="small">
                <CloseIcon fontSize="small" />
              </IconButton>
            </Box>

            {selectedRow && (
              <Box>
                <Grid container spacing={{ xs: 1, sm: 1.5 }} sx={{ mb: 2 }}>
                  <InfoRow
                    icon={PersonIcon}
                    label="Farmer"
                    value={selectedRow.farmer.name}
                    xs={12}
                    sm={6}
                    bold
                  />
                  <InfoRow
                    icon={PhoneIcon}
                    label="Mobile"
                    value={selectedRow.farmer.mobile}
                    xs={12}
                    sm={6}
                    bold
                    color="#1976D2"
                  />
                  <InfoRow
                    icon={MapIcon}
                    label="Address"
                    value={selectedRow.farmer.address}
                    xs={12}
                  />
                  <InfoRow
                    icon={MapIcon}
                    label="District"
                    value={selectedRow.farmer.district}
                    xs={4}
                    sm={4}
                  />
                  <InfoRow
                    icon={MapIcon}
                    label="Block"
                    value={selectedRow.farmer.block}
                    xs={4}
                    sm={4}
                  />
                  <InfoRow
                    icon={MapIcon}
                    label="Village"
                    value={selectedRow.farmer.village}
                    xs={4}
                    sm={4}
                  />
                  <InfoRow
                    icon={AssignmentIcon}
                    label="Scheme"
                    value={selectedRow.scheme.name}
                    xs={12}
                    sm={6}
                    bold
                  />
                  <InfoRow
                    icon={NatureIcon}
                    label="Production Center"
                    value={selectedRow.production_center.name}
                    xs={12}
                    sm={6}
                  />
                </Grid>

                <Typography
                  variant="subtitle2"
                  fontWeight={700}
                  sx={{ mb: 1, fontSize: { xs: "0.8rem", sm: "0.9rem" } }}
                >
                  Sapling Details
                </Typography>
                <TableContainer
                  component={Paper}
                  sx={{
                    mb: 2,
                    border: "1px solid #eee",
                    borderRadius: 2,
                    overflow: "hidden",
                  }}
                >
                  <Table size="small">
                    <TableHead>
                      <TableRow sx={{ bgcolor: "#f5f7f6" }}>
                        <TableCell
                          sx={{
                            fontWeight: "bold",
                            fontSize: { xs: "0.7rem", sm: "0.8rem" },
                            py: { xs: 0.8, sm: 1 },
                          }}
                        >
                          Species
                        </TableCell>
                        <TableCell
                          sx={{
                            fontWeight: "bold",
                            fontSize: { xs: "0.7rem", sm: "0.8rem" },
                            py: { xs: 0.8, sm: 1 },
                          }}
                        >
                          Tamil
                        </TableCell>
                        <TableCell
                          align="right"
                          sx={{
                            fontWeight: "bold",
                            fontSize: { xs: "0.7rem", sm: "0.8rem" },
                            py: { xs: 0.8, sm: 1 },
                          }}
                        >
                          Qty
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {selectedRow.items.map((i, idx) => (
                        <TableRow key={idx}>
                          <TableCell
                            sx={{
                              fontSize: { xs: "0.72rem", sm: "0.8rem" },
                              py: { xs: 0.5, sm: 0.8 },
                            }}
                          >
                            {i.species_name}
                          </TableCell>
                          <TableCell
                            sx={{
                              fontSize: { xs: "0.72rem", sm: "0.8rem" },
                              py: { xs: 0.5, sm: 0.8 },
                            }}
                          >
                            {i.species_name_tamil}
                          </TableCell>
                          <TableCell
                            align="right"
                            sx={{
                              fontSize: { xs: "0.72rem", sm: "0.8rem" },
                              fontWeight: 600,
                              py: { xs: 0.5, sm: 0.8 },
                            }}
                          >
                            {i.final_quantity || 0}
                          </TableCell>
                        </TableRow>
                      ))}
                      <TableRow sx={{ bgcolor: "#e8f5e9" }}>
                        <TableCell
                          colSpan={2}
                          align="right"
                          sx={{
                            fontWeight: "bold",
                            fontSize: { xs: "0.75rem", sm: "0.85rem" },
                            py: { xs: 0.6, sm: 1 },
                          }}
                        >
                          Total:
                        </TableCell>
                        <TableCell
                          align="right"
                          sx={{
                            fontWeight: "bold",
                            fontSize: { xs: "0.75rem", sm: "0.85rem" },
                            color: colors.darkGreen,
                            py: { xs: 0.6, sm: 1 },
                          }}
                        >
                          {totalSaplings(selectedRow.items)}
                        </TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </TableContainer>

                <Paper
                  sx={{
                    p: { xs: 1.5, sm: 2 },
                    mb: 2,
                    bgcolor: "#f5f7f6",
                    borderRadius: 2,
                  }}
                >
                  <Typography
                    variant="subtitle2"
                    fontWeight={700}
                    sx={{ mb: 1, fontSize: "0.85rem" }}
                  >
                    Status
                  </Typography>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1.5,
                      flexWrap: "wrap",
                    }}
                  >
                    {getStatusChip(selectedRow.status)}
                    {selectedRow.inspected_by && (
                      <Typography
                        variant="body2"
                        sx={{ fontSize: { xs: "0.72rem", sm: "0.85rem" } }}
                      >
                        by <b>{selectedRow.inspected_by}</b> on{" "}
                        {formatDate(selectedRow.inspected_date)}
                      </Typography>
                    )}
                  </Box>
                </Paper>

                {selectedRow.inspection_image && (
                  <Box sx={{ mb: 2 }}>
                    <Typography
                      variant="subtitle2"
                      fontWeight={700}
                      sx={{ mb: 1, fontSize: "0.85rem" }}
                    >
                      Latest Photo
                    </Typography>
                    <Grid container spacing={{ xs: 1.5, sm: 2 }}>
                      <Grid item xs={12} sm={7}>
                        <Box
                          sx={{
                            border: "1px solid #eee",
                            borderRadius: 2,
                            overflow: "hidden",
                          }}
                        >
                          <img
                            src={selectedRow.inspection_image}
                            alt="Inspection"
                            style={{
                              width: "100%",
                              height: { xs: 180, sm: 200 },
                              objectFit: "cover",
                            }}
                          />
                        </Box>
                      </Grid>
                      <Grid item xs={12} sm={5}>
                        <Paper
                          sx={{
                            p: { xs: 1.5, sm: 2 },
                            height: "100%",
                            borderRadius: 2,
                            bgcolor: "#f9fafb",
                          }}
                        >
                          <Typography
                            variant="subtitle2"
                            fontWeight={600}
                            sx={{ fontSize: "0.8rem" }}
                          >
                            Survey Info
                          </Typography>
                          <Typography
                            variant="body2"
                            sx={{ fontSize: "0.8rem", mt: 0.5 }}
                          >
                            Survived:{" "}
                            <b style={{ color: colors.darkGreen }}>
                              {selectedRow.surveyed_count}
                            </b>
                          </Typography>
                          {selectedRow.inspection_history?.[0]?.uploads?.[0]
                            ?.inspection_address && (
                            <Box
                              sx={{
                                mt: 1,
                                display: "flex",
                                alignItems: "flex-start",
                                gap: 0.5,
                              }}
                            >
                              <LocationIcon
                                sx={{
                                  fontSize: 16,
                                  color: "#999",
                                  mt: 0.2,
                                  flexShrink: 0,
                                }}
                              />
                              <Typography
                                variant="caption"
                                sx={{
                                  fontSize: "0.7rem",
                                  color: "#666",
                                  lineHeight: 1.4,
                                }}
                              >
                                {
                                  selectedRow.inspection_history[0].uploads[0]
                                    .inspection_address
                                }
                              </Typography>
                            </Box>
                          )}
                        </Paper>
                      </Grid>
                    </Grid>
                  </Box>
                )}

                {selectedRow.inspection_history &&
                  selectedRow.inspection_history.length > 0 && (
                    <Box sx={{ mb: 2 }}>
                      <Typography
                        variant="subtitle2"
                        fontWeight={700}
                        sx={{ mb: 2, fontSize: "0.85rem" }}
                      >
                        <HistoryIcon
                          sx={{
                            verticalAlign: "middle",
                            mr: 0.5,
                            fontSize: 18,
                          }}
                        />{" "}
                        History
                      </Typography>
                      <CustomTimeline
                        items={buildTimelineItems(
                          selectedRow.inspection_history,
                        )}
                      />
                    </Box>
                  )}

                {isBlockInspector && canInspect(selectedRow) && (
                  <>
                    <Divider sx={{ my: 2 }} />
                    <Box sx={{ display: "flex", justifyContent: "center" }}>
                      <Button
                        variant="contained"
                        startIcon={
                          selectedRow.inspection_id ? (
                            <UploadIcon sx={{ fontSize: 18 }} />
                          ) : (
                            <AddIcon sx={{ fontSize: 18 }} />
                          )
                        }
                        onClick={() => {
                          closeModal("view");
                          setTimeout(
                            () => openModal("upload", selectedRow),
                            300,
                          );
                        }}
                        disabled={!isRealMobile}
                        sx={{
                          bgcolor: !isRealMobile
                            ? "#9e9e9e"
                            : colors.darkPurple,
                          "&:hover": {
                            bgcolor: !isRealMobile ? "#9e9e9e" : "#6A1B9A",
                          },
                          fontSize: { xs: "0.8rem", sm: "0.85rem" },
                          borderRadius: 2,
                          px: 3,
                          py: 1,
                          boxShadow: isRealMobile
                            ? "0 2px 8px rgba(123,31,162,0.3)"
                            : "none",
                        }}
                      >
                        {selectedRow.inspection_id
                          ? "Re-inspect"
                          : "Start Inspection"}
                      </Button>
                    </Box>
                    {!isRealMobile && (
                      <Typography
                        variant="caption"
                        color="error"
                        sx={{ display: "block", textAlign: "center", mt: 1 }}
                      >
                        ⚠️ Mobile device required for inspection
                      </Typography>
                    )}
                  </>
                )}

                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "center",
                    mt: 3,
                    pb: { xs: 2, sm: 0 },
                  }}
                >
                  <Button
                    variant="outlined"
                    onClick={() => closeModal("view")}
                    sx={{ fontSize: "0.85rem", borderRadius: 2, px: 4 }}
                  >
                    Close
                  </Button>
                </Box>
              </Box>
            )}
          </Paper>
        </Box>
      </Modal>

      {/* ===== DESKTOP BLOCK ALERT DIALOG ===== */}
      <Modal
        open={desktopBlockAlert}
        onClose={() => setDesktopBlockAlert(false)}
      >
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: { xs: "90%", sm: 420 },
          }}
        >
          <Paper sx={{ borderRadius: 3, overflow: "hidden" }}>
            <Box
              sx={{
                bgcolor: "#fff3e0",
                p: 3,
                display: "flex",
                alignItems: "center",
                gap: 2,
              }}
            >
              <Box
                sx={{
                  width: 60,
                  height: 60,
                  borderRadius: "50%",
                  bgcolor: "#ff9800",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flexShrink: 0,
                }}
              >
                <BlockIcon sx={{ fontSize: 32, color: "white" }} />
              </Box>
              <Box>
                <Typography variant="h6" fontWeight="bold" color="#e65100">
                  Mobile Device Required
                </Typography>
                <Typography variant="body2" color="#bf360c">
                  Inspection upload is restricted to mobile devices
                </Typography>
              </Box>
            </Box>
            <Box sx={{ p: 3 }}>
              <Typography variant="body1" gutterBottom sx={{ mb: 2 }}>
                Field inspection requires:
              </Typography>
              <Box component="ul" sx={{ pl: 2, m: 0 }}>
                <Box
                  component="li"
                  sx={{
                    mb: 1.5,
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                  }}
                >
                  <CameraIcon sx={{ fontSize: 18, color: colors.darkGreen }} />
                  <Typography variant="body2">
                    Camera access for geo-tagged photo capture
                  </Typography>
                </Box>
                <Box
                  component="li"
                  sx={{
                    mb: 1.5,
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                  }}
                >
                  <LocationIcon
                    sx={{ fontSize: 18, color: colors.darkGreen }}
                  />
                  <Typography variant="body2">
                    GPS for location verification (within 10m)
                  </Typography>
                </Box>
                <Box
                  component="li"
                  sx={{
                    mb: 1.5,
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                  }}
                >
                  <PhoneAndroidIcon
                    sx={{ fontSize: 18, color: colors.darkGreen }}
                  />
                  <Typography variant="body2">
                    Native device features for on-site verification
                  </Typography>
                </Box>
              </Box>
              <Alert severity="info" sx={{ mt: 2, borderRadius: 2 }}>
                <Typography variant="body2">
                  <strong>Desktop users:</strong> You can view details, but
                  cannot upload new inspection photos.
                </Typography>
              </Alert>
            </Box>
            <Box
              sx={{ p: 2, pt: 0, display: "flex", justifyContent: "flex-end" }}
            >
              <Button
                onClick={() => setDesktopBlockAlert(false)}
                variant="contained"
                sx={{
                  bgcolor: colors.darkGreen,
                  "&:hover": { bgcolor: "#1B5E20" },
                  fontWeight: "bold",
                  borderRadius: 2,
                }}
              >
                Understood
              </Button>
            </Box>
          </Paper>
        </Box>
      </Modal>

      {/* ===== MOBILE BOTTOM FAB ===== */}
      {isMobile && (
        <Zoom in>
          <Fab
            color="primary"
            onClick={() => setFilterDrawerOpen(true)}
            sx={{
              position: "fixed",
              bottom: 20,
              right: 20,
              bgcolor: colors.darkGreen,
              width: 50,
              height: 50,
              "&:hover": { bgcolor: "#1B5E20" },
              boxShadow: "0 4px 14px rgba(27,94,32,0.4)",
              zIndex: 1000,
            }}
          >
            <Badge
              badgeContent={isAnyFilterActive ? 1 : 0}
              color="error"
              sx={{ "& .MuiBadge-badge": { top: -2, right: -2 } }}
            >
              <TuneIcon sx={{ fontSize: 22 }} />
            </Badge>
          </Fab>
        </Zoom>
      )}
    </Box>
  );
};
export default Inspection;

// import React, { useState, useMemo, useEffect } from "react";
// import {
//   Box,
//   Typography,
//   Paper,
//   Chip,
//   TextField,
//   Button,
//   Select,
//   FormControl,
//   InputLabel,
//   Grid,
//   Divider,
//   Skeleton,
//   Tooltip,
//   CircularProgress,
//   MenuItem,
//   IconButton,
//   Drawer,
//   SwipeableDrawer,
//   useMediaQuery,
//   useTheme,
//   Fab,
//   Zoom,
//   Collapse,
//   Stack,
//   Badge,
// } from "@mui/material";
// import {
//   FilterList as FilterIcon,
//   Visibility as ViewIcon,
//   CheckCircle as CompletedIcon,
//   InfoOutlined as InfoOutlinedIcon,
//   AddCircle as AddIcon,
//   Clear as ClearIcon,
//   Close as CloseIcon,
//   Tune as TuneIcon,
//   FilterAlt as FilterAltIcon,
//   Person as PersonIcon,
//   Map as MapIcon,
//   Assignment as AssignmentIcon,
//   CalendarMonth as CalendarIcon,
//   PendingRounded as PendingIcon,
// } from "@mui/icons-material";
// import { useUser } from "../../Context/UserContext";
// import { GetApi } from "../../Services/api/ApiCall";
// import API_URLS from "../../Services/api/action";
// import DynamicTable from "../Dynamic/DynamicTable";
// import InspectionDetails from "./inspection/InspectionDetails";
// import InspectionUpload from "./inspection/InspectionUpload";
// import {
//   colors,
//   formatDate,
//   totalSaplings,
//   InfoRow,
// } from "./InspectionConstants";

// const Inspection = () => {
//   const theme = useTheme();
//   const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
//   const isTablet = useMediaQuery(theme.breakpoints.between("sm", "md"));
//   const isDesktop = useMediaQuery(theme.breakpoints.up("md"));

//   const { authData, token } = useUser();

//   // --- STATE ---
//   const [data, setData] = useState([]);
//   const [loading, setLoading] = useState(true);
//   const [filterStatus, setFilterStatus] = useState("All");
//   const [filterDateFrom, setFilterDateFrom] = useState("");
//   const [filterDateTo, setFilterDateTo] = useState("");
//   const [page, setPage] = useState(0);
//   const [rowsPerPage, setRowsPerPage] = useState(10);
//   const [totalRecords, setTotalRecords] = useState(0);

//   const [modals, setModals] = useState({ upload: false, view: false });
//   const [selectedRow, setSelectedRow] = useState(null);
//   const [filterDrawerOpen, setFilterDrawerOpen] = useState(false);

//   // Role Checks
//   const isBlockInspector = authData?.role === "block_admin";
//   const isViewer = [
//     "superadmin",
//     "department_admin",
//     "district_admin",
//   ].includes(authData?.role);

//   // --- DATA LOGIC ---
//   const mapApiToUi = (apiOrder) => {
//     const inspections = apiOrder.inspections || [];
//     const sortedInspections = [...inspections].sort((a, b) => b.id - a.id);
//     const latestInspection =
//       sortedInspections.length > 0 ? sortedInspections[0] : null;

//     let currentStatus = "Not Inspected";
//     let inspectedDate = null;
//     let inspectedByName = null;
//     let inspectionImg = null;
//     let surveyCount = 0;
//     let latestInspectionId = null;

//     if (
//       latestInspection &&
//       latestInspection.uploads &&
//       latestInspection.uploads.length > 0
//     ) {
//       latestInspectionId = latestInspection.id;
//       inspectedDate = latestInspection.inspection_date;
//       const latestUpload = latestInspection.uploads[0];
//       inspectedByName = latestUpload.inspected_by_name || "Unknown";
//       inspectionImg = latestUpload.image;
//       surveyCount = latestUpload.survey_count || 0;
//       currentStatus = "Inspected";
//     }

//     const itemsList = apiOrder.items || [];
//     return {
//       id: apiOrder.order_id,
//       order_id: apiOrder.order_id,
//       request_id: apiOrder.items?.[0]?.request_id || null,
//       status: currentStatus,
//       inspected_by: inspectedByName,
//       inspected_date: inspectedDate,
//       inspection_image: inspectionImg,
//       inspection_id: latestInspectionId,
//       surveyed_count: surveyCount,
//       inspection_history: sortedInspections.map((insp) => ({
//         id: insp.id,
//         inspection_date: insp.inspection_date,
//         inspector_name: insp.uploads?.[0]?.inspected_by_name || "Unknown",
//         uploads: insp.uploads || [],
//         remarks: insp.remarks,
//       })),
//       items_summary:
//         itemsList.length > 0
//           ? itemsList
//               .map((i) => `${i.species_name} (${i.final_quantity || 0})`)
//               .join(", ")
//           : "No items",
//       farmer: {
//         id: apiOrder.farmer?.id,
//         name: apiOrder.farmer?.name || "Unknown",
//         mobile: apiOrder.farmer?.mobile || "N/A",
//         address: apiOrder.farmer?.address || "N/A",
//         district: apiOrder.farmer?.location?.district || "N/A",
//         block: apiOrder.farmer?.location?.block || "N/A",
//         village: apiOrder.farmer?.location?.village || "N/A",
//       },
//       production_center: {
//         name: apiOrder.production_center?.name || "Unknown PC",
//         address: apiOrder.production_center?.address || "N/A",
//         village_id: apiOrder.production_center?.village_id,
//       },
//       scheme: { name: apiOrder.scheme?.name || "N/A" },
//       items: itemsList.map((item) => ({
//         stock_id: item.stock_id,
//         species_name: item.species_name,
//         species_name_tamil: item.species_name_tamil || item.species_name,
//         final_quantity: item.final_quantity || 0,
//       })),
//       inspections,
//     };
//   };

//   useEffect(() => {
//     const fetchOrders = async () => {
//       if (!authData?.user_id || !authData?.role) return;
//       setLoading(true);
//       try {
//         const url = `${API_URLS.GetFormersOrders}${authData.user_id}/${authData.role}?page=${page + 1}&limit=${rowsPerPage}`;
//         const res = await GetApi(url, token);
//         if (res?.success && res.data) {
//           setTotalRecords(res.total_records || 0);
//           if (page === 0) setData(res.data.map(mapApiToUi));
//           else
//             setData((prev) => [
//               ...prev,
//               ...res.data
//                 .filter((item) => !prev.some((p) => p.id === item.order_id))
//                 .map(mapApiToUi),
//             ]);
//         }
//       } catch (err) {
//         console.error(err);
//       } finally {
//         setLoading(false);
//       }
//     };
//     fetchOrders();
//   }, [authData.user_id, authData.role, token, page, rowsPerPage]);

//   const refetchData = () => {
//     // Simple refetch: reset to page 0 to ensure consistency or just re-run current page
//     // Here we simply re-trigger the effect logic by resetting page if needed, or just calling the API logic inline
//     // For simplicity in this refactor, we reset to page 0
//     setPage(0);
//   };

//   const filteredData = useMemo(() => {
//     let result = [...data];
//     if (filterStatus !== "All")
//       result = result.filter((r) => r.status === filterStatus);
//     if (filterDateFrom || filterDateTo) {
//       result = result.filter((r) => {
//         const rowDate = r.inspected_date ? new Date(r.inspected_date) : null;
//         if (!rowDate) return false;
//         const fromDate = filterDateFrom ? new Date(filterDateFrom) : null;
//         const toDate = filterDateTo ? new Date(filterDateTo) : null;
//         if (fromDate) {
//           fromDate.setHours(0, 0, 0, 0);
//           if (rowDate < fromDate) return false;
//         }
//         if (toDate) {
//           toDate.setHours(23, 59, 59, 999);
//           if (rowDate > toDate) return false;
//         }
//         return true;
//       });
//     }
//     // Sorting: Not inspected first
//     result.sort((a, b) => {
//       if (a.status === "Not Inspected" && b.status !== "Not Inspected")
//         return -1;
//       if (a.status !== "Not Inspected" && b.status === "Not Inspected")
//         return 1;
//       return 0;
//     });
//     return result;
//   }, [data, filterStatus, filterDateFrom, filterDateTo]);

//   const stats = useMemo(
//     () => ({
//       total: data.length,
//       notInspected: data.filter((d) => d.status === "Not Inspected").length,
//       inspected: data.filter((d) => d.status === "Inspected").length,
//     }),
//     [data],
//   );

//   // --- ACTIONS ---
//   const openModal = (type, row) => {
//     setSelectedRow(row);
//     setModals((p) => ({ ...p, [type]: true }));
//   };

//   const closeModal = (type) => {
//     setModals((p) => ({ ...p, [type]: false }));
//   };

//   const clearAllFilters = () => {
//     setFilterStatus("All");
//     setFilterDateFrom("");
//     setFilterDateTo("");
//     setFilterDrawerOpen(false);
//   };

//   const isAnyFilterActive =
//     filterStatus !== "All" || filterDateFrom || filterDateTo;

//   const getStatusChip = (status) => {
//     return (
//       <Chip
//         label={status}
//         size="small"
//         sx={{
//           fontWeight: 600,
//           fontSize: { xs: "0.65rem", sm: "0.75rem" },
//           height: { xs: 22, sm: 26 },
//           bgcolor:
//             status === "Inspected" ? colors.paleGreen : colors.paleOrange,
//           color: status === "Inspected" ? colors.darkGreen : colors.darkOrange,
//         }}
//       />
//     );
//   };

//   // --- UI HELPERS ---
//   const FilterDrawerContent = () => (
//     <Box
//       sx={{
//         p: { xs: 2.5, sm: 3 },
//         height: "100%",
//         display: "flex",
//         flexDirection: "column",
//       }}
//     >
//       <Box
//         sx={{
//           display: "flex",
//           alignItems: "center",
//           justifyContent: "space-between",
//           mb: 3,
//         }}
//       >
//         <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
//           <FilterAltIcon sx={{ color: colors.darkGreen, fontSize: 22 }} />
//           <Typography
//             variant="h6"
//             fontWeight={700}
//             sx={{ fontSize: { xs: "1.1rem", sm: "1.25rem" } }}
//           >
//             Filters
//           </Typography>
//           {isAnyFilterActive && (
//             <Chip
//               label="Active"
//               size="small"
//               color="primary"
//               variant="outlined"
//               sx={{ height: 20, fontSize: "0.6rem", fontWeight: 700 }}
//             />
//           )}
//         </Box>
//         <IconButton onClick={() => setFilterDrawerOpen(false)} size="small">
//           <CloseIcon fontSize="small" />
//         </IconButton>
//       </Box>

//       <Box sx={{ mb: 3 }}>
//         <Typography
//           variant="subtitle2"
//           fontWeight={700}
//           sx={{ mb: 1, fontSize: "0.8rem", color: "#000" }}
//         >
//           STATUS
//         </Typography>
//         <FormControl fullWidth size="small" variant="outlined">
//           <InputLabel
//             sx={{
//               fontSize: "0.85rem",
//               color: "#000",
//               fontWeight: 500,
//               "&.Mui-focused": { color: "#000" },
//             }}
//           >
//             Status
//           </InputLabel>
//           <Select
//             value={filterStatus}
//             label="Status"
//             onChange={(e) => setFilterStatus(e.target.value)}
//             sx={{
//               "& .MuiOutlinedInput-notchedOutline": {
//                 borderColor: "#4caf50",
//                 borderWidth: 1.5,
//               },
//             }}
//           >
//             <MenuItem value="All">All Status</MenuItem>
//             <MenuItem value="Not Inspected">Not Inspected</MenuItem>
//             <MenuItem value="Inspected">Inspected</MenuItem>
//           </Select>
//         </FormControl>
//       </Box>

//       <Box sx={{ mb: 3 }}>
//         <Typography
//           variant="subtitle2"
//           fontWeight={700}
//           sx={{ mb: 1, fontSize: "0.8rem", color: "#000" }}
//         >
//           INSPECTED FROM
//         </Typography>
//         <TextField
//           fullWidth
//           size="small"
//           type="date"
//           value={filterDateFrom}
//           onChange={(e) => setFilterDateFrom(e.target.value)}
//           InputLabelProps={{ shrink: true }}
//           inputProps={{ max: filterDateTo || undefined }}
//         />
//       </Box>

//       <Box sx={{ mb: 3 }}>
//         <Typography
//           variant="subtitle2"
//           fontWeight={700}
//           sx={{ mb: 1, fontSize: "0.8rem", color: "#000" }}
//         >
//           INSPECTED TO
//         </Typography>
//         <TextField
//           fullWidth
//           size="small"
//           type="date"
//           value={filterDateTo}
//           onChange={(e) => setFilterDateTo(e.target.value)}
//           InputLabelProps={{ shrink: true }}
//           inputProps={{ min: filterDateFrom || undefined }}
//         />
//       </Box>

//       <Box sx={{ flex: 1 }} />
//       <Box
//         sx={{
//           pt: 2,
//           borderTop: "1px solid #eee",
//           display: "flex",
//           flexDirection: "column",
//           gap: 1.5,
//         }}
//       >
//         <Button
//           fullWidth
//           variant="contained"
//           onClick={() => setFilterDrawerOpen(false)}
//           sx={{
//             bgcolor: colors.darkGreen,
//             fontWeight: 700,
//             py: 1.2,
//             borderRadius: 2,
//           }}
//         >
//           Apply Filters ({filteredData.length})
//         </Button>
//         {isAnyFilterActive && (
//           <Button
//             fullWidth
//             variant="outlined"
//             startIcon={<ClearIcon />}
//             onClick={clearAllFilters}
//           >
//             Clear All
//           </Button>
//         )}
//       </Box>
//     </Box>
//   );

//   const tableColumns = useMemo(() => {
//     const baseColumns = [
//       {
//         field: "farmer",
//         headerName: "Farmer / Order",
//         render: (row) => (
//           <Box>
//             <Typography fontWeight={700} fontSize="0.85rem">
//               {row.farmer.name}
//             </Typography>
//             <Typography variant="caption" color="text.secondary">
//               {row.order_id}
//             </Typography>
//           </Box>
//         ),
//       },
//       {
//         field: "address",
//         headerName: "Address",
//         render: (row) => (
//           <Box>
//             <Typography variant="body2" noWrap sx={{ maxWidth: 180 }}>
//               {row.farmer.address}
//             </Typography>
//             <Typography variant="caption" color="text.secondary">
//               {row.farmer.district}/{row.farmer.block}
//             </Typography>
//           </Box>
//         ),
//       },
//       {
//         field: "saplings",
//         headerName: "Saplings",
//         render: (row) => (
//           <Box>
//             <Typography
//               variant="caption"
//               sx={{ fontWeight: 700, color: colors.darkGreen }}
//             >
//               {totalSaplings(row.items)}
//             </Typography>
//             {row.surveyed_count > 0 && (
//               <Typography
//                 variant="caption"
//                 color="text.secondary"
//                 display="block"
//               >
//                 Surv: {row.surveyed_count}
//               </Typography>
//             )}
//           </Box>
//         ),
//       },
//       {
//         field: "status",
//         headerName: "Status",
//         render: (row) => getStatusChip(row.status),
//       },
//     ];

//     if (isViewer) {
//       baseColumns.splice(3, 0, {
//         field: "inspected_by",
//         headerName: "Inspected By",
//         render: (row) => (
//           <Box>
//             <Typography variant="caption">{row.inspected_by || "-"}</Typography>
//             {row.inspected_date && (
//               <Typography
//                 variant="caption"
//                 color="text.secondary"
//                 display="block"
//               >
//                 {formatDate(row.inspected_date)}
//               </Typography>
//             )}
//           </Box>
//         ),
//       });
//     }
//     return baseColumns;
//   }, [isViewer]);

//   return (
//     <Box
//       sx={{
//         p: { xs: 1.5, sm: 2, md: 3 },
//         bgcolor: "#f5f7f6",
//         minHeight: "100%",
//         pb: isMobile ? 10 : 3,
//       }}
//     >
//       {/* Header */}
//       <Box
//         sx={{
//           mb: { xs: 2, sm: 3 },
//           display: "flex",
//           alignItems: { xs: "center", sm: "flex-start" },
//           justifyContent: "space-between",
//           gap: 1,
//         }}
//       >
//         <Box sx={{ minWidth: 0 }}>
//           <Typography
//             variant="h5"
//             fontWeight={800}
//             color="#1B5E20"
//             sx={{ fontSize: { xs: "1.15rem", sm: "1.4rem", md: "1.5rem" } }}
//           >
//             {isBlockInspector
//               ? " Block Inspection Tasks"
//               : " Inspection Overview"}
//           </Typography>
//           <Typography
//             variant="body2"
//             color="text.secondary"
//             sx={{ fontSize: { xs: "0.7rem", sm: "0.8rem" }, mt: 0.3 }}
//           >
//             {isBlockInspector
//               ? "View and inspect orders assigned to your block"
//               : "View inspection status submitted by Block Admins"}
//           </Typography>
//         </Box>
//         {isMobile && (
//           <Zoom in>
//             <Badge
//               badgeContent={isAnyFilterActive ? 1 : 0}
//               color="primary"
//               variant="dot"
//             >
//               <Fab
//                 size="small"
//                 color="primary"
//                 onClick={() => setFilterDrawerOpen(true)}
//                 sx={{ bgcolor: colors.darkGreen }}
//               >
//                 <TuneIcon />
//               </Fab>
//             </Badge>
//           </Zoom>
//         )}
//       </Box>

//       {/* Stats */}
//       <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1.5, mb: 4 }}>
//         {[
//           {
//             label: "Total Orders",
//             value: stats.total,
//             color: "#4F46E5",
//             bg: "rgba(79, 70, 229, 0.08)",
//             icon: AssignmentIcon,
//           },
//           {
//             label: "Not Inspected",
//             value: stats.notInspected,
//             color: "#F97316",
//             bg: "rgba(249, 115, 22, 0.10)",
//             icon: PendingIcon,
//           },
//           {
//             label: "Completed",
//             value: stats.inspected,
//             color: "#16A34A",
//             bg: "rgba(22, 163, 74, 0.10)",
//             icon: CompletedIcon,
//           },
//         ].map((stat, idx) => (
//           <Box
//             key={idx}
//             sx={{
//               flex: {
//                 xs: "1 1 calc(33.33% - 8px)",
//                 sm: "1 1 calc(33.33% - 12px)",
//               },
//               display: "flex",
//               justifyContent: "center",
//             }}
//           >
//             <Paper
//               sx={{
//                 width: "100%",
//                 p: { xs: 1, sm: 1.5 },
//                 borderRadius: 2,
//                 textAlign: "center",
//                 bgcolor: stat.bg,
//               }}
//             >
//               <stat.icon
//                 sx={{
//                   fontSize: { xs: 16, sm: 20 },
//                   color: stat.color,
//                   mb: 1,
//                   opacity: 0.7,
//                 }}
//               />
//               <Typography
//                 sx={{
//                   color: stat.color,
//                   fontWeight: 700,
//                   fontSize: { xs: "1rem", sm: "1.2rem" },
//                 }}
//               >
//                 {stat.value}
//               </Typography>
//               <Typography sx={{ fontSize: "12px", color: "#000" }}>
//                 {stat.label}
//               </Typography>
//             </Paper>
//           </Box>
//         ))}
//       </Box>

//       {/* Desktop Filters */}
//       {isDesktop && (
//         <Paper sx={{ p: 2.5, mb: 3, borderRadius: 3, bgcolor: "#fff" }}>
//           <Box
//             sx={{
//               display: "flex",
//               alignItems: "center",
//               justifyContent: "space-between",
//               mb: 2,
//             }}
//           >
//             <Typography
//               variant="subtitle2"
//               fontWeight={700}
//               sx={{ display: "flex", alignItems: "center", gap: 0.5 }}
//             >
//               <FilterIcon /> Filters
//             </Typography>
//             {isAnyFilterActive && (
//               <Button
//                 size="small"
//                 variant="text"
//                 startIcon={<ClearIcon />}
//                 onClick={clearAllFilters}
//               >
//                 Clear All
//               </Button>
//             )}
//           </Box>
//           <Grid container spacing={2}>
//             <Grid item xs={4}>
//               <FormControl fullWidth size="small">
//                 <InputLabel>Status</InputLabel>
//                 <Select
//                   value={filterStatus}
//                   label="Status"
//                   onChange={(e) => setFilterStatus(e.target.value)}
//                 >
//                   <MenuItem value="All">All</MenuItem>
//                   <MenuItem value="Not Inspected">Not Inspected</MenuItem>
//                   <MenuItem value="Inspected">Inspected</MenuItem>
//                 </Select>
//               </FormControl>
//             </Grid>
//             <Grid item xs={4}>
//               <TextField
//                 fullWidth
//                 size="small"
//                 type="date"
//                 label="From"
//                 value={filterDateFrom}
//                 onChange={(e) => setFilterDateFrom(e.target.value)}
//                 InputLabelProps={{ shrink: true }}
//               />
//             </Grid>
//             <Grid item xs={4}>
//               <TextField
//                 fullWidth
//                 size="small"
//                 type="date"
//                 label="To"
//                 value={filterDateTo}
//                 onChange={(e) => setFilterDateTo(e.target.value)}
//                 InputLabelProps={{ shrink: true }}
//               />
//             </Grid>
//           </Grid>
//         </Paper>
//       )}

//       {/* Mobile Filter Pills */}
//       {(isMobile || isTablet) && isAnyFilterActive && (
//         <Box sx={{ display: "flex", gap: 1, mb: 2, overflowX: "auto" }}>
//           {filterStatus !== "All" && (
//             <Chip
//               label={filterStatus}
//               onDelete={() => setFilterStatus("All")}
//               size="small"
//             />
//           )}
//           {filterDateFrom && (
//             <Chip
//               label={`From: ${filterDateFrom}`}
//               onDelete={() => setFilterDateFrom("")}
//               size="small"
//             />
//           )}
//           {filterDateTo && (
//             <Chip
//               label={`To: ${filterDateTo}`}
//               onDelete={() => setFilterDateTo("")}
//               size="small"
//             />
//           )}
//         </Box>
//       )}

//       {/* Desktop Table */}
//       <Box sx={{ display: { xs: "none", md: "block" } }}>
//         <DynamicTable
//           data={filteredData}
//           columns={[
//             ...tableColumns,
//             {
//               field: "actions",
//               headerName: "Actions",
//               render: (row) => (
//                 <Box
//                   sx={{ display: "flex", gap: 0.5, justifyContent: "center" }}
//                 >
//                   <IconButton
//                     size="small"
//                     color="primary"
//                     onClick={() => openModal("view", row)}
//                   >
//                     <ViewIcon />
//                   </IconButton>
//                   {isBlockInspector && (
//                     <Tooltip
//                       title={
//                         row.inspection_id ? "Re-inspect" : "Start Inspection"
//                       }
//                     >
//                       <IconButton
//                         size="small"
//                         onClick={() => openModal("upload", row)}
//                         sx={{ color: colors.darkPurple }}
//                       >
//                         {row.inspection_id ? <UploadIcon /> : <AddIcon />}
//                       </IconButton>
//                     </Tooltip>
//                   )}
//                 </Box>
//               ),
//             },
//           ]}
//           loading={loading}
//           rowsPerPageOptions={[5, 10, 25]}
//           defaultRowsPerPage={10}
//           enableSearch={true}
//         />
//       </Box>

//       {/* Mobile Cards */}
//       <Box sx={{ display: { xs: "block", md: "none" } }}>
//         {loading ? (
//           [1, 2, 3].map((i) => (
//             <Paper key={i} sx={{ p: 2, mb: 2 }}>
//               <Skeleton variant="rect" height={20} width="60%" />
//               <Skeleton variant="rect" height={60} sx={{ my: 1 }} />
//             </Paper>
//           ))
//         ) : filteredData.length === 0 ? (
//           <Paper sx={{ p: 3, textAlign: "center" }}>
//             <Typography>No Records Found</Typography>
//           </Paper>
//         ) : (
//           filteredData.map((row) => (
//             <Paper
//               key={row.id}
//               sx={{ mb: 1.5, borderRadius: 1.5, overflow: "hidden" }}
//             >
//               <Box
//                 sx={{
//                   bgcolor:
//                     row.status === "Inspected"
//                       ? colors.paleGreen
//                       : colors.paleOrange,
//                   px: 1.5,
//                   py: 1,
//                   display: "flex",
//                   justifyContent: "space-between",
//                 }}
//               >
//                 <Box>
//                   <Typography fontWeight={700} sx={{ fontSize: "0.85rem" }}>
//                     {row.farmer.name}
//                   </Typography>
//                   <Typography variant="caption" sx={{ fontSize: "0.6rem" }}>
//                     {row.order_id}
//                   </Typography>
//                 </Box>
//                 {getStatusChip(row.status)}
//               </Box>
//               <Box sx={{ p: 1.5 }}>
//                 <Grid container spacing={1}>
//                   <InfoRow
//                     icon={MapIcon}
//                     label="Address"
//                     value={row.farmer.address}
//                     xs={12}
//                   />
//                   <InfoRow
//                     icon={CalendarIcon}
//                     label="Inspected"
//                     value={
//                       row.inspected_date ? formatDate(row.inspected_date) : "—"
//                     }
//                     xs={6}
//                   />
//                   {isViewer && row.inspected_by && (
//                     <InfoRow
//                       icon={PersonIcon}
//                       label="Inspected By"
//                       value={row.inspected_by}
//                       xs={6}
//                     />
//                   )}
//                 </Grid>
//                 <Box
//                   sx={{
//                     display: "flex",
//                     justifyContent: "flex-end",
//                     gap: 1,
//                     mt: 1,
//                   }}
//                 >
//                   <Button
//                     size="small"
//                     variant="outlined"
//                     startIcon={<ViewIcon sx={{ fontSize: 16 }} />}
//                     onClick={() => openModal("view", row)}
//                   >
//                     View
//                   </Button>
//                   {isBlockInspector && (
//                     <Button
//                       size="small"
//                       variant="contained"
//                       startIcon={
//                         row.inspection_id ? (
//                           <UploadIcon sx={{ fontSize: 16 }} />
//                         ) : (
//                           <AddIcon sx={{ fontSize: 16 }} />
//                         )
//                       }
//                       onClick={() => openModal("upload", row)}
//                       sx={{ bgcolor: colors.darkPurple }}
//                     >
//                       {row.inspection_id ? "Re-inspect" : "Inspect"}
//                     </Button>
//                   )}
//                 </Box>
//               </Box>
//             </Paper>
//           ))
//         )}
//       </Box>

//       {/* Drawer */}
//       <SwipeableDrawer
//         anchor="bottom"
//         open={filterDrawerOpen}
//         onClose={() => setFilterDrawerOpen(false)}
//         onOpen={() => setFilterDrawerOpen(true)}
//         sx={{ "& .MuiDrawer-paper": { borderRadius: "16px 16px 0 0" } }}
//       >
//         <FilterDrawerContent />
//       </SwipeableDrawer>

//       {/* Modals */}
//       <InspectionDetails
//         open={modals.view}
//         row={selectedRow}
//         onClose={() => closeModal("view")}
//         isBlockInspector={isBlockInspector}
//         isRealMobile={window?.isRealMobileDevice?.()} // simple check or pass prop
//         onUploadClick={(r) => {
//           setSelectedRow(r);
//           setModals({ upload: true, view: false });
//         }}
//       />

//       <InspectionUpload
//         open={modals.upload}
//         row={selectedRow}
//         onClose={() => closeModal("upload")}
//         authData={authData}
//         token={token}
//         onSuccess={refetchData}
//       />

//       {/* FAB */}
//       {isMobile && (
//         <Zoom in>
//           <Fab
//             color="primary"
//             onClick={() => setFilterDrawerOpen(true)}
//             sx={{
//               position: "fixed",
//               bottom: 20,
//               right: 20,
//               bgcolor: colors.darkGreen,
//             }}
//           >
//             <Badge badgeContent={isAnyFilterActive ? 1 : 0} color="error">
//               <TuneIcon />
//             </Badge>
//           </Fab>
//         </Zoom>
//       )}
//     </Box>
//   );
// };

// export default Inspection;
